import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import type { OrderItem, MenuItem, Category, Order, OrderType, Table, Payment, Customer, SmartpayTerminal, IntegrationSettings, StaffMember, EventStallOperator } from '../types';
import { getAvailableQuantity } from '../lib/dailyAvailability';
import MenuItemImage from '../components/menu/MenuItemImage';
import ShoppingCartIcon from '../components/icons/ShoppingCartIcon';
import XCircleIcon from '../components/icons/XCircleIcon';
import CreditCardIcon from '../components/icons/CreditCardIcon';
import PrinterIcon from '../components/icons/PrinterIcon';
import FullscreenIcon from '../components/icons/FullscreenIcon';
import ExitFullscreenIcon from '../components/icons/ExitFullscreenIcon';
import PrintableReceipt from '../components/pos/PrintableReceipt';
import PaymentModal from './PaymentModal';
import RegisterClosedGuard from '../components/pos/RegisterClosedGuard';
import MeatOptionModal from '../components/pos/MeatOptionModal';
import LiveFishModal, { type LiveFishSale } from '../components/pos/LiveFishModal';
import ReceiptQrModal from '../components/pos/ReceiptQrModal';
import { useFullscreen } from '../hooks/useFullscreen';
import { verifyEventOperatorPin } from '../lib/eventOperatorPin';
import { resolveKitchenAlertSoundUrl } from '../lib/kitchenAlertSound';
import { collectEventTerminalIdsFromStall } from '../lib/normalizeEventStallSettings';
import { orderCreatedAtMs, toMillis } from '../lib/orderTimestamps';
import { orderMatchesEventProfile } from '../lib/eventOrderScope';
import { buildEventTokenLabelById, getEventTokenDisplayLabel, physicalSlipFromSequence } from '../lib/eventOrderTokens';
import { isAdmin } from '../lib/authUtils';
import {
  filterCategoriesForOrderType,
  filterMenuItemsForOrderType,
} from '../lib/menuChannelAvailability';
import { isCompletedPosOrder, isPendingPosOrder, orderMatchesFloorPlanTable } from '../lib/orderUtils';
import UberDoordashOrderModal, { type UberDoordashPlatform } from '../components/pos/UberDoordashOrderModal';
import WaitstaffInstallHint from '../components/waitstaff/WaitstaffInstallHint';
import WaitstaffTicketCard, { waitstaffOrderTitle } from '../components/waitstaff/WaitstaffTicketCard';
import {
  buildWaitstaffOrderDraft,
  clearWaitstaffOrderDraft,
  readWaitstaffOrderDraft,
  waitstaffDraftHasWork,
  waitstaffDraftSummary,
  writeWaitstaffOrderDraft,
  type WaitstaffDraftOrderType,
} from '../lib/waitstaffOrderDraft';
import KitchenDisplayPage from './KitchenDisplayPage';
import MagnifyingGlassIcon from '../components/icons/MagnifyingGlassIcon';
import type { CurrentUser, KitchenSLASettings, RegisterSession } from '../types';
import { formatLiveFishDetail, isWeighedFishLine, liveFishLineTotal } from '../lib/liveFishPrice';

const EVENT_OP_SESSION_KEY = 'kingspark_event_operator_session';

function readEventOperatorSession(): { operatorId: string; operatorName: string } | null {
  try {
    const raw = sessionStorage.getItem(EVENT_OP_SESSION_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p?.operatorId || !p?.operatorName) return null;
    return { operatorId: String(p.operatorId), operatorName: String(p.operatorName) };
  } catch {
    return null;
  }
}

function writeEventOperatorSession(s: { operatorId: string; operatorName: string }) {
  sessionStorage.setItem(EVENT_OP_SESSION_KEY, JSON.stringify(s));
}

function clearEventOperatorSession() {
  sessionStorage.removeItem(EVENT_OP_SESSION_KEY);
}

/** Normalize Firestore pickup timestamps (seconds vs ms). */
function normalizeEventPickupMs(raw: unknown): number | null {
  if (typeof raw !== 'number' || Number.isNaN(raw)) return null;
  return raw < 1_000_000_000_000 ? raw * 1000 : raw;
}

/** Banner text for event queue tokens (post-reset uses #n; pre-reset uses Ln). */
function formatEventTokenBanner(label: string): string {
  if (!label) return '';
  return label.startsWith('L') ? `Token ${label}` : `Token #${label}`;
}

function eventTokenShortBadge(label: string): string {
  if (!label) return '?';
  return label.startsWith('L') ? label : `#${label}`;
}

function mergeEventCategoryOrder(uniqueNames: string[], savedOrder: string[]): string[] {
  const set = new Set(uniqueNames.filter(Boolean));
  const out: string[] = [];
  for (const s of savedOrder) {
    const t = String(s ?? '').trim();
    if (t && set.has(t)) {
      out.push(t);
      set.delete(t);
    }
  }
  const rest = [...set].sort((a, b) => a.localeCompare(b));
  return [...out, ...rest];
}

interface MobilePOSPageProps {
  onOpenRegister?: (openingFloat: number) => void;
  onRequireManagerPin?: (action: 'open' | 'close', onApproved: () => void) => void;
  onPlaceOrder: (items: OrderItem[], orderType: OrderType, details: {
    tableId?: number;
    customerId?: number;
    customerNumber?: number;
    customerName?: string;
    notes?: string;
    appliedReward?: any;
    manualDiscount?: number;
    discountRequiresApproval?: boolean;
    buffetType?: 'breakfast' | 'lunch' | 'dinner';
    buffetStartTime?: number;
    buffetEndTime?: number;
    eventFulfillmentType?: 'eat-now' | 'takeaway';
    pickupTime?: number;
    eventOperatorId?: string;
    eventOperatorName?: string;
    eventId?: string;
    eventNameSnapshot?: string;
    eventMenuSnapshotId?: string;
    eventCostSnapshot?: Record<string, number>;
    /** Slip number issued for this placement (stored on order). */
    eventQueueToken?: number;
    deliveryInfo?: { platform: 'Uber Eats' | 'DoorDash'; customerName: string };
  }) => Order | void | Promise<Order | void>;
  onUpdateOrder: (order: Order) => void | Order | Promise<void | Order>;
  onProcessPayment: (orderId: string, payment: Payment) => void;
  menuItems: MenuItem[];
  categories: Category[];
  orders: Order[];
  tables: Table[];
  terminals: SmartpayTerminal[];
  registerSession: { id: string } | undefined;
  integrationSettings?: IntegrationSettings;
  currentUser?: StaffMember;
  customers: Customer[];
  /** False until App.tsx's `subscribeActive` listener has reported once. */
  registerSessionsHydrated?: boolean;
  /** Phone waitstaff: floor POS shell (no desktop chrome). */
  waitstaffMode?: boolean;
  onWaitstaffLock?: () => void;
  onUpdateOrderItemStatus?: (
    orderId: string,
    itemIndex: number,
    status: 'pending' | 'completed',
    quantityCompleted?: number,
    batchIndex?: number,
    targetQtyForCard?: number,
  ) => void;
  onStartOrder?: (orderId: string, batchIndex?: number) => void;
  onHideOrderFromKitchen?: (orderId: string) => Promise<void>;
  slaSettings?: Partial<KitchenSLASettings>;
}

type OrderTypeOption = 'dine-in' | 'takeaway' | 'delivery' | 'buffet' | 'uber-doordash';
type WaitstaffTab = 'home' | 'order' | 'pending' | 'completed' | 'kitchen';

/** True when every non-removed line has been toggled on (ready for handoff; order may still be open). */
function eventOrderAllItemsChecked(order: Order): boolean {
  const visible = (order.items ?? []).filter((item) => !item.removed);
  return visible.length > 0 && visible.every((item) => item.served);
}

const MobilePOSPage: React.FC<MobilePOSPageProps> = ({
  onOpenRegister,
  onPlaceOrder,
  onUpdateOrder,
  onProcessPayment,
  menuItems,
  categories,
  orders,
  tables,
  terminals,
  registerSession,
  integrationSettings,
  currentUser,
  customers,
  registerSessionsHydrated = true,
  waitstaffMode = false,
  onWaitstaffLock,
  onUpdateOrderItemStatus,
  onStartOrder,
  onHideOrderFromKitchen,
  slaSettings,
}) => {
  const eventStall = integrationSettings?.eventStall;
  const activeEventProfile = useMemo(
    () => (eventStall?.events ?? []).find((evt) => evt.id === eventStall?.activeEventId) ?? null,
    [eventStall?.events, eventStall?.activeEventId]
  );
  const eventTokenCounterResetAtMs = useMemo(() => toMillis(eventStall?.tokenCounterResetAt), [eventStall?.tokenCounterResetAt]);
  const eventOperatorsConfigured = useMemo<EventStallOperator[]>(
    () => (Array.isArray(eventStall?.eventOperators) ? eventStall!.eventOperators! : []),
    [eventStall?.eventOperators]
  );
  const validEventItems = useMemo(
    () =>
      ((activeEventProfile?.menuSnapshot ?? eventStall?.eventMenuItems) ?? [])
        .filter((item) => item.isAvailable !== false)
        .filter((item) => Number.isFinite(Number(item.price)) && Number(item.price) > 0)
        .filter((item) => String(item.name ?? '').trim().length > 0 && String(item.categoryName ?? '').trim().length > 0)
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a.name.localeCompare(b.name)),
    [activeEventProfile?.menuSnapshot, eventStall?.eventMenuItems]
  );
  const stallSoldOutByItemId = useMemo(() => {
    const m = new Map<number, boolean>();
    validEventItems.forEach((row) => {
      if (row.soldOut) m.set(Number(row.id), true);
    });
    return m;
  }, [validEventItems]);
  const eventUnitCostByItemId = useMemo(() => {
    const map = new Map<number, number>();
    validEventItems.forEach((row) => {
      const id = Number(row.id);
      const c = (row as { unitCost?: number }).unitCost;
      if (Number.isFinite(id) && c != null && Number.isFinite(Number(c)) && Number(c) >= 0) {
        map.set(id, Math.round(Number(c) * 100) / 100);
      }
    });
    return map;
  }, [validEventItems]);

  const eventModeAvailable = !!eventStall?.enabled;
  const eventMenuConfigured = validEventItems.length > 0;
  type MobileViewMode = 'event' | 'restaurant' | 'service' | 'scheduled' | 'completed';
  const [mobileViewMode, setMobileViewMode] = useState<MobileViewMode>(waitstaffMode ? 'restaurant' : 'event');
  const isEventMode = !waitstaffMode && mobileViewMode === 'event';
  const isServiceMode = !waitstaffMode && mobileViewMode === 'service';
  const isScheduledMode = !waitstaffMode && mobileViewMode === 'scheduled';
  const isCompletedMode = !waitstaffMode && mobileViewMode === 'completed';
  const [waitstaffTab, setWaitstaffTab] = useState<WaitstaffTab>('home');
  const waitstaffHome = waitstaffMode && waitstaffTab === 'home';
  const waitstaffKitchen = waitstaffMode && waitstaffTab === 'kitchen';
  const waitstaffPending = waitstaffMode && waitstaffTab === 'pending';
  const waitstaffCompletedList = waitstaffMode && waitstaffTab === 'completed';
  const [orderType, setOrderType] = useState<OrderTypeOption | null>(waitstaffMode ? null : null);
  const [uberDoordashDetails, setUberDoordashDetails] = useState<{ platform: UberDoordashPlatform; customerName: string } | null>(null);
  const [showUberDoordashModal, setShowUberDoordashModal] = useState(false);
  const [deliveryCustomerName, setDeliveryCustomerName] = useState('');
  const [serviceOrderDetail, setServiceOrderDetail] = useState<Order | null>(null);
  const [completedOrderDetail, setCompletedOrderDetail] = useState<Order | null>(null);
  const [receiptQrOrder, setReceiptQrOrder] = useState<Order | null>(null);
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [notes, setNotes] = useState('');
  const [eventFulfillmentType, setEventFulfillmentType] = useState<'eat-now' | 'takeaway'>('eat-now');
  const [isScheduledEventOrder, setIsScheduledEventOrder] = useState(false);
  const [eventPickupTimeInput, setEventPickupTimeInput] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [customAddPrice, setCustomAddPrice] = useState('');
  const menuScrollRef = useRef<HTMLElement>(null);
  const categorySectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const categoryChipRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const suppressScrollSpyUntilRef = useRef(0);
  const [showCartSheet, setShowCartSheet] = useState(false);
  const [showTableSelect, setShowTableSelect] = useState(false);
  const [showTablesOverview, setShowTablesOverview] = useState(false);
  const [showOrderSelect, setShowOrderSelect] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [placedOrder, setPlacedOrder] = useState<Order | null>(null);
  const [orderToUpdate, setOrderToUpdate] = useState<Order | null>(null);
  const [editingEventOrder, setEditingEventOrder] = useState<Order | null>(null);
  const [isPlacing, setIsPlacing] = useState(false);
  const [meatOptionItem, setMeatOptionItem] = useState<MenuItem | null>(null);
  const [liveFishItem, setLiveFishItem] = useState<MenuItem | null>(null);
  const [paymentOrder, setPaymentOrder] = useState<Order | null>(null);
  const [orderToPrint, setOrderToPrint] = useState<Order | null>(null);
  const receiptRef = useRef<HTMLDivElement>(null);
  const waitstaffDraftHydratedRef = useRef(false);
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  const [nowMs, setNowMs] = useState(() => Date.now());

  const [eventOpSession, setEventOpSession] = useState(() => readEventOperatorSession());
  const [unlockOperatorId, setUnlockOperatorId] = useState('');
  const [unlockPin, setUnlockPin] = useState('');
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [pickupSoonLabel, setPickupSoonLabel] = useState<string | null>(null);
  const pickupAlertedIdsRef = useRef<Set<string>>(new Set());
  /** Optimistic line-item patches so Service toggles feel instant before Firestore catches up. */
  const [eventOrderItemPatch, setEventOrderItemPatch] = useState<Record<string, OrderItem[]>>({});

  const operatorsGateActive = eventModeAvailable && eventOperatorsConfigured.length > 0;
  const operatorGateBlocked = operatorsGateActive && !eventOpSession;

  /** Event lists (unpaid / service / scheduled) scoped to logged-in operator when PIN operators are enabled */
  const ordersForEventSessionUi = useMemo(() => {
    if (!operatorsGateActive || !eventOpSession) return orders;
    return orders.filter((o) => {
      if (o.orderType !== 'event') return true;
      const oid = o.eventOperatorId;
      if (oid == null || oid === '') return true;
      return oid === eventOpSession.operatorId;
    });
  }, [orders, operatorsGateActive, eventOpSession]);

  const ordersForEventUi = useMemo(
    () =>
      ordersForEventSessionUi.map((o) => {
        const patched = eventOrderItemPatch[o.id];
        return patched ? { ...o, items: patched } : o;
      }),
    [ordersForEventSessionUi, eventOrderItemPatch]
  );

  /** Until payment modal is dismissed once, hide bottom Print/Payment strip so staff use cart Pay now first */
  const [eventPaymentUiDismissed, setEventPaymentUiDismissed] = useState(true);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const playPickupLeadAlert = useCallback(() => {
    const s = integrationSettings?.kitchenSLA;
    if (s?.alertSoundEnabled === false) return;
    try {
      const soundUrl = resolveKitchenAlertSoundUrl(s?.alertSoundUrl);
      const volPct = s?.alertSoundVolume ?? 100;
      const audio = new Audio(soundUrl);
      audio.volume = Math.min(1, Math.max(0, volPct / 100));
      audio.play().catch(() => { /* autoplay / interrupted */ });
    } catch {
      /* autoplay / missing file */
    }
  }, [integrationSettings?.kitchenSLA]);

  const businessInfo = integrationSettings?.businessInfo;

  const eventMenuItems = useMemo<(MenuItem & { stallSoldOut?: boolean })[]>(() => {
    if (!eventModeAvailable) return [];
    return validEventItems.map((item) => ({
      id: item.id,
      name: item.name,
      price: Number(item.price),
      categoryId: item.categoryName,
      imageUrl: item.imageUrl || '',
      imageUrlThumb: item.imageUrl || '',
      isAvailable: item.isAvailable !== false,
      description: `${eventStall?.name || 'Event'} item`,
      openingQuantity: item.openingQuantity,
      stallSoldOut: !!item.soldOut,
      displayOrder: item.displayOrder ?? 0,
      dailyAvailability: undefined,
      hasDailyAvailability: false,
      dailyAvailabilityLimit: undefined,
      lastAvailabilityReset: undefined,
      isSignatureDish: false,
      allergens: undefined,
      allergyWarning: undefined,
    }));
  }, [eventModeAvailable, eventStall?.name, validEventItems]);

  const eventCategoriesResolved = useMemo(() => {
    if (!eventModeAvailable || validEventItems.length === 0) return [] as { id: string; name: string }[];
    const names = [...new Set(validEventItems.map((i) => String(i.categoryName ?? '').trim()).filter(Boolean))];
    const merged = mergeEventCategoryOrder(names, eventStall?.eventCategoryOrder ?? []);
    return merged.map((name) => ({ id: name, name }));
  }, [eventModeAvailable, validEventItems, eventStall?.eventCategoryOrder]);

  const effectiveMenuItems = isEventMode && eventModeAvailable
    ? eventMenuItems
    : filterMenuItemsForOrderType(menuItems, categories, orderType);
  const effectiveCategories = isEventMode && eventModeAvailable
    ? eventCategoriesResolved
    : filterCategoriesForOrderType(categories, menuItems, orderType);

  useEffect(() => {
    if (isEventMode && eventModeAvailable) return;
    if (effectiveCategories.length === 0) return;
    setSelectedCategoryId((prev) => {
      if (prev != null && effectiveCategories.some((c) => String(c.id) === String(prev))) {
        return prev;
      }
      return effectiveCategories[0]?.id ?? null;
    });
  }, [effectiveCategories, isEventMode, eventModeAvailable]);

  const matchesMenuSearch = useCallback((item: MenuItem, q: string) => {
    if (!q) return true;
    return item.name?.toLowerCase().includes(q) || (item.description?.toLowerCase().includes(q) ?? false);
  }, []);

  const searchFilteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return effectiveMenuItems;
    return effectiveMenuItems.filter((item) => matchesMenuSearch(item, q));
  }, [effectiveMenuItems, searchQuery, matchesMenuSearch]);

  const filteredItems = useMemo(() => {
    let list = searchFilteredItems;
    if (isEventMode && eventModeAvailable && selectedCategoryId != null) {
      const cid = selectedCategoryId;
      list = list.filter(
        (item) =>
          String(item.categoryId) === String(cid) ||
          (typeof item.categoryId === 'number' && typeof cid === 'number' && item.categoryId === cid),
      );
    }
    return list;
  }, [searchFilteredItems, isEventMode, eventModeAvailable, selectedCategoryId]);

  /** Event mode: group filtered items by category for display (unless a category pill filters to one). */
  const eventMenuGroupedSections = useMemo(() => {
    if (!isEventMode || !eventModeAvailable || selectedCategoryId != null) return null;
    const byCat = new Map<string, MenuItem[]>();
    filteredItems.forEach((item) => {
      const k = String(item.categoryId ?? 'Other');
      const list = byCat.get(k) ?? [];
      list.push(item);
      byCat.set(k, list);
    });
    const keys = mergeEventCategoryOrder([...byCat.keys()], eventStall?.eventCategoryOrder ?? []);
    return keys
      .map((categoryLabel) => {
        const items = (byCat.get(categoryLabel) ?? []).slice().sort((a, b) => {
          const da = (a as MenuItem & { displayOrder?: number }).displayOrder ?? 0;
          const db = (b as MenuItem & { displayOrder?: number }).displayOrder ?? 0;
          return da - db || String(a.name).localeCompare(String(b.name));
        });
        return { categoryLabel, items };
      })
      .filter((s) => s.items.length > 0);
  }, [isEventMode, eventModeAvailable, selectedCategoryId, filteredItems, eventStall?.eventCategoryOrder]);

  const restaurantMenuGroupedSections = useMemo(() => {
    if (isEventMode && eventModeAvailable) return null;
    const byCat = new Map<string, MenuItem[]>();
    searchFilteredItems.forEach((item) => {
      const k = String(item.categoryId ?? '');
      const list = byCat.get(k) ?? [];
      list.push(item);
      byCat.set(k, list);
    });
    const sortItems = (items: MenuItem[]) =>
      items.slice().sort((a, b) => {
        const da = (a as MenuItem & { displayOrder?: number }).displayOrder ?? 0;
        const db = (b as MenuItem & { displayOrder?: number }).displayOrder ?? 0;
        return da - db || String(a.name).localeCompare(String(b.name));
      });
    const sections = effectiveCategories
      .filter((cat) => byCat.has(String(cat.id)))
      .map((cat) => ({
        categoryId: String(cat.id),
        categoryLabel: cat.name,
        items: sortItems(byCat.get(String(cat.id)) ?? []),
      }));
    const known = new Set(effectiveCategories.map((c) => String(c.id)));
    const leftoverKeys = [...byCat.keys()].filter((k) => k && !known.has(k));
    leftoverKeys.forEach((k) => {
      sections.push({ categoryId: k, categoryLabel: 'Other', items: sortItems(byCat.get(k) ?? []) });
    });
    const uncategorized = byCat.get('') ?? [];
    if (uncategorized.length) {
      sections.push({ categoryId: '_none', categoryLabel: 'Other', items: sortItems(uncategorized) });
    }
    return sections.filter((s) => s.items.length > 0);
  }, [isEventMode, eventModeAvailable, searchFilteredItems, effectiveCategories]);

  const menuSections = useMemo(() => {
    if (isEventMode && eventModeAvailable) {
      if (eventMenuGroupedSections) {
        return eventMenuGroupedSections.map((s) => ({
          categoryId: s.categoryLabel,
          categoryLabel: s.categoryLabel,
          items: s.items,
        }));
      }
      return [{ categoryId: '', categoryLabel: '', items: filteredItems }];
    }
    return restaurantMenuGroupedSections ?? [{ categoryId: '', categoryLabel: '', items: searchFilteredItems }];
  }, [
    isEventMode,
    eventModeAvailable,
    eventMenuGroupedSections,
    filteredItems,
    restaurantMenuGroupedSections,
    searchFilteredItems,
  ]);

  const scrollToCategory = useCallback((categoryId: number | string) => {
    const key = String(categoryId);
    setSelectedCategoryId(categoryId);
    suppressScrollSpyUntilRef.current = Date.now() + 700;
    const el = categorySectionRefs.current[key];
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    categoryChipRefs.current[key]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, []);

  useEffect(() => {
    const root = menuScrollRef.current;
    if (!root) return;
    const nodes = Object.values(categorySectionRefs.current).filter((n): n is HTMLElement => !!n);
    if (nodes.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < suppressScrollSpyUntilRef.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const id = visible[0]?.target.getAttribute('data-category-id');
        if (!id || id === '_none') return;
        setSelectedCategoryId((prev) => (String(prev) === id ? prev : id));
        const chip = categoryChipRefs.current[id];
        chip?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      },
      { root, rootMargin: '-12% 0px -70% 0px', threshold: [0, 0.15, 0.4] },
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [menuSections, waitstaffTab, isServiceMode, isScheduledMode, isCompletedMode]);

  const totalItems = orderItems.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = orderItems.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const tax = subtotal * (15 / 115);
  const total = subtotal;

  const resolvedPaymentOrder = useMemo(() => {
    const source = paymentOrder || placedOrder;
    if (!source) return null;
    return orders.find((o) => o.id === source.id) || source;
  }, [orders, paymentOrder, placedOrder]);

  /** Live unpaid event order for cancel (synced with Firestore subscription). */
  const cancelTargetEventOrder = useMemo(() => {
    const raw = paymentOrder ?? placedOrder ?? editingEventOrder;
    if (!raw || raw.orderType !== 'event' || raw.paymentStatus !== 'unpaid') return null;
    return orders.find((o) => o.id === raw.id) ?? raw;
  }, [paymentOrder, placedOrder, editingEventOrder, orders]);

  /** Event orders: only configured Event terminals (never fall back to restaurant terminals). Other orders: exclude Event-only terminals when Event mode is enabled. */
  const paymentModalTerminals = useMemo(() => {
    const all = terminals ?? [];
    const orderType = resolvedPaymentOrder?.orderType;
    const stall = eventStall;
    const eventIds = collectEventTerminalIdsFromStall(stall);
    const eventEnabled = !!stall?.enabled;

    if (orderType === 'event') {
      if (!eventEnabled) {
        return { list: all, notice: undefined as string | undefined };
      }
      if (eventIds.length === 0) {
        return {
          list: [] as SmartpayTerminal[],
          notice:
            'No Event terminals configured. In Settings → Event / Stall, select one or more Smartpay terminals for Event card payments.',
        };
      }
      const allowed = new Set(eventIds);
      const list = all.filter((t) => allowed.has(t.id));
      if (list.length === 0) {
        return {
          list: [] as SmartpayTerminal[],
          notice:
            'Configured Event terminals were not found in your Smartpay terminal list. Check Settings → Integrations → Smartpay and Event terminal assignment.',
        };
      }
      return { list, notice: undefined as string | undefined };
    }

    if (eventEnabled && eventIds.length > 0) {
      const excluded = new Set(eventIds);
      const list = all.filter((t) => !excluded.has(t.id));
      if (list.length === 0) {
        return {
          list: [] as SmartpayTerminal[],
          notice:
            'All Smartpay terminals are assigned to Event mode. Add a separate terminal for restaurant/mobile orders or adjust Event terminal assignment in Settings.',
        };
      }
      return { list, notice: undefined as string | undefined };
    }

    return { list: all, notice: undefined as string | undefined };
  }, [terminals, eventStall, resolvedPaymentOrder?.orderType]);

  const getEventAvailableUnits = (menuItemId: number): number | null => {
    if (!isEventMode || !eventModeAvailable) return null;
    if (!eventStockRemainingByItemId.has(menuItemId)) return null; // unlimited
    const baseRemaining = eventStockRemainingByItemId.get(menuItemId) ?? 0;
    const editingAllowance = editingEventOriginalQtyByItemId.get(menuItemId) ?? 0;
    return Math.max(0, baseRemaining + editingAllowance);
  };

  const addToCart = (item: MenuItem, selectedMeatOption?: string) => {
    if (isEventMode && eventModeAvailable && stallSoldOutByItemId.get(Number(item.id))) {
      alert(`"${item.name}" is marked sold out.`);
      return;
    }
    const availableUnits = getEventAvailableUnits(Number(item.id));
    const currentQty = orderItems
      .filter((i) => Number(i.id) === Number(item.id))
      .reduce((sum, i) => sum + i.quantity, 0);
    if (availableUnits != null && currentQty >= availableUnits) {
      alert(`No more "${item.name}" available for this event.`);
      return;
    }
    if (item.hasDailyAvailability) {
      const available = getAvailableQuantity(item, orders);
      if (available <= 0) {
        alert('This item is finished for today.');
        return;
      }
    }
    if (item.soldByWeight) {
      setLiveFishItem(item);
      return;
    }
    if (item.meatOptions && item.meatOptions.length > 0 && !selectedMeatOption) {
      setMeatOptionItem(item);
      return;
    }
    setOrderItems((prev) => {
      const existing = prev.find(
        (i) => i.id === item.id && (i as any).selectedMeatOption === selectedMeatOption
      );
      if (existing) {
        return prev.map((i) =>
          i.id === item.id && (i as any).selectedMeatOption === selectedMeatOption
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      return [...prev, { ...item, quantity: 1, status: 'pending' as const, selectedMeatOption } as OrderItem];
    });
    setMeatOptionItem(null);
  };

  const addCustomFromSearch = () => {
    const name = searchQuery.trim();
    const price = Number(customAddPrice);
    if (!name) return;
    if (!Number.isFinite(price) || price < 0) {
      alert('Enter a valid price to add a custom item.');
      return;
    }
    setOrderItems((prev) => [
      ...prev,
      {
        id: -Date.now(),
        name,
        price,
        quantity: 1,
        status: 'pending' as const,
        categoryId: 0,
        description: 'Custom',
      } as OrderItem,
    ]);
    setSearchQuery('');
    setCustomAddPrice('');
  };

  const confirmLiveFish = (item: MenuItem, sale: LiveFishSale) => {
    setLiveFishItem(null);
    const total = liveFishLineTotal(sale.weightKg, sale.pricePerKg);
    if (total == null) return;
    if (isEventMode && eventModeAvailable && stallSoldOutByItemId.get(Number(item.id))) {
      alert(`"${item.name}" is marked sold out.`);
      return;
    }
    const catalog = { ...item };
    delete catalog.weightFishOptions;
    delete catalog.meatOptions;
    setOrderItems((prev) => [
      ...prev,
      {
        ...catalog,
        price: total,
        quantity: 1,
        status: 'pending' as const,
        soldByWeight: true,
        selectedFish: sale.fish,
        weightKg: sale.weightKg,
        pricePerKg: sale.pricePerKg,
        lineId: `fish-${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      },
    ]);
  };

  const removeFromCart = (index: number) => {
    setOrderItems((prev) => prev.filter((_, i) => i !== index));
  };

  const updateQuantity = (index: number, delta: number) => {
    setOrderItems((prev) => {
      const next = [...prev];
      const item = next[index];
      if (isWeighedFishLine(item) && delta > 0) return prev;
      const newQty = item.quantity + delta;
      if (newQty <= 0) {
        next.splice(index, 1);
        return next;
      }
      next[index] = { ...item, quantity: newQty };
      return next;
    });
  };

  /** Sum of cart quantities for this menu item (all variants / meat options share the same product id). */
  const cartQuantityForMenuItem = (menuItem: MenuItem) =>
    orderItems.filter((oi) => oi.id === menuItem.id).reduce((sum, oi) => sum + oi.quantity, 0);

  const decrementMenuItemFromCart = (menuItem: MenuItem) => {
    for (let i = orderItems.length - 1; i >= 0; i--) {
      if (orderItems[i].id === menuItem.id) {
        updateQuantity(i, -1);
        return;
      }
    }
  };

  const activeUnpaidDineIn = useMemo(
    () =>
      orders.filter(
        (o) =>
          o.orderType === 'dine-in' &&
          o.paymentStatus === 'unpaid' &&
          o.status !== 'cancelled' &&
          o.status !== 'refunded'
      ),
    [orders]
  );

  const activeUnpaidTakeaway = useMemo(
    () =>
      orders.filter(
        (o) =>
          o.orderType === 'takeaway' &&
          o.paymentStatus === 'unpaid' &&
          o.status !== 'cancelled' &&
          o.status !== 'refunded'
      ),
    [orders]
  );

  const activeUnpaidRestaurantOrders = useMemo(
    () =>
      [...activeUnpaidDineIn, ...activeUnpaidTakeaway].sort(
        (a, b) => orderCreatedAtMs(b) - orderCreatedAtMs(a)
      ),
    [activeUnpaidDineIn, activeUnpaidTakeaway]
  );

  const waitstaffPendingOrders = useMemo(
    () => orders.filter((o) => o.orderType !== 'event' && isPendingPosOrder(o))
      .sort((a, b) => orderCreatedAtMs(b) - orderCreatedAtMs(a)),
    [orders],
  );

  const waitstaffCompletedOrders = useMemo(
    () => orders.filter((o) => o.orderType !== 'event' && isCompletedPosOrder(o))
      .sort((a, b) => orderCreatedAtMs(b) - orderCreatedAtMs(a)),
    [orders],
  );

  const activeUnpaidEventOrders = useMemo(
    () =>
      ordersForEventUi
        .filter(
          (o) =>
            o.orderType === 'event' &&
            o.paymentStatus === 'unpaid' &&
            o.status !== 'cancelled' &&
            o.status !== 'refunded'
        )
        .sort((a, b) => orderCreatedAtMs(a) - orderCreatedAtMs(b) || String(a.id).localeCompare(String(b.id))),
    [ordersForEventUi]
  );

  const eventTokenLabelById = useMemo(
    () => buildEventTokenLabelById(orders, eventStall?.tokenCounterResetAt, eventStall?.eventTokenRollMax),
    [orders, eventStall?.tokenCounterResetAt, eventStall?.eventTokenRollMax],
  );

  useEffect(() => {
    if (!eventStall?.enabled) return;
    const TEN_MS = 10 * 60 * 1000;
    const upcoming = ordersForEventUi.filter((o) => {
      if (o.orderType !== 'event' || o.paymentStatus !== 'paid') return false;
      if (o.status === 'served' || o.status === 'cancelled' || o.status === 'refunded') return false;
      const pickupMs = normalizeEventPickupMs(o.pickupTime);
      if (pickupMs == null) return false;
      const delta = pickupMs - nowMs;
      return delta > 0 && delta <= TEN_MS;
    });
    let toast: string | null = null;
    const alerted = pickupAlertedIdsRef.current;
    upcoming.forEach((o) => {
      const pickupMs = normalizeEventPickupMs(o.pickupTime)!;
      const mins = Math.max(1, Math.ceil((pickupMs - nowMs) / 60000));
      const tokenBit = getEventTokenDisplayLabel(o, eventTokenLabelById);
      toast = `Pickup in ~${mins} min · ${tokenBit ? formatEventTokenBanner(tokenBit) : 'Token ?'}`;
      if (!alerted.has(o.id)) {
        alerted.add(o.id);
        playPickupLeadAlert();
      }
    });
    setPickupSoonLabel(toast);
  }, [ordersForEventUi, nowMs, eventStall?.enabled, playPickupLeadAlert, eventTokenLabelById]);

  useEffect(() => {
    const idsWithPickup = new Set(
      ordersForEventUi
        .filter((o) => {
          if (o.orderType !== 'event' || o.paymentStatus !== 'paid') return false;
          if (o.status === 'served' || o.status === 'cancelled' || o.status === 'refunded') return false;
          return normalizeEventPickupMs(o.pickupTime) != null;
        })
        .map((o) => o.id),
    );
    const alerted = pickupAlertedIdsRef.current;
    [...alerted].forEach((id) => {
      if (!idsWithPickup.has(id)) alerted.delete(id);
    });
  }, [ordersForEventUi]);

  const nextEventTokenNumber = useMemo(() => {
    const resetMs = eventTokenCounterResetAtMs;
    const seq =
      resetMs > 0
        ? orders.filter((o) => o.orderType === 'event' && orderCreatedAtMs(o) >= resetMs).length + 1
        : orders.filter((o) => o.orderType === 'event').length + 1;
    return physicalSlipFromSequence(seq, eventStall?.eventTokenRollMax);
  }, [orders, eventTokenCounterResetAtMs, eventStall?.eventTokenRollMax]);

  const eventStockRemainingByItemId = useMemo(() => {
    const soldById = new Map<number, number>();
    const profileId = activeEventProfile?.id;
    orders
      .filter((o) => o.orderType === 'event' && o.status !== 'cancelled' && o.status !== 'refunded')
      .filter((o) => orderMatchesEventProfile(o, profileId))
      .forEach((order) => {
        (order.items ?? []).forEach((line) => {
          if (line.removed) return;
          const itemId = Number(line.id);
          if (!Number.isFinite(itemId)) return;
          soldById.set(itemId, (soldById.get(itemId) ?? 0) + (Number(line.quantity) || 0));
        });
      });
    const remaining = new Map<number, number>();
    (eventStall?.eventMenuItems ?? []).forEach((item) => {
      if (typeof item.openingQuantity !== 'number') return;
      const opening = Math.max(0, Math.floor(item.openingQuantity));
      const sold = soldById.get(Number(item.id)) ?? 0;
      remaining.set(Number(item.id), Math.max(0, opening - sold));
    });
    return remaining;
  }, [orders, eventStall?.eventMenuItems, activeEventProfile?.id]);

  const editingEventOriginalQtyByItemId = useMemo(() => {
    const map = new Map<number, number>();
    if (!editingEventOrder) return map;
    (editingEventOrder.items ?? []).forEach((line) => {
      if (line.removed) return;
      const id = Number(line.id);
      if (!Number.isFinite(id)) return;
      map.set(id, (map.get(id) ?? 0) + (Number(line.quantity) || 0));
    });
    return map;
  }, [editingEventOrder]);

  const paidEventOrdersToServe = useMemo(
    () =>
      ordersForEventUi
        .filter((o) => {
          if (
            o.orderType !== 'event' ||
            o.paymentStatus !== 'paid' ||
            o.status === 'served' ||
            o.status === 'cancelled' ||
            o.status === 'refunded'
          ) {
            return false;
          }
          const pickupMs = normalizeEventPickupMs(o.pickupTime);
          return pickupMs == null;
        })
        .sort((a, b) => orderCreatedAtMs(a) - orderCreatedAtMs(b) || String(a.id).localeCompare(String(b.id))),
    [ordersForEventUi]
  );

  const scheduledEventOrders = useMemo(
    () =>
      ordersForEventUi
        .filter((o) => {
          if (
            o.orderType !== 'event' ||
            o.paymentStatus !== 'paid' ||
            o.status === 'served' ||
            o.status === 'cancelled' ||
            o.status === 'refunded'
          ) {
            return false;
          }
          return normalizeEventPickupMs(o.pickupTime) != null;
        })
        .sort((a, b) => normalizeEventPickupMs(a.pickupTime)! - normalizeEventPickupMs(b.pickupTime)!),
    [ordersForEventUi]
  );

  const eventAdminVisible = isAdmin(currentUser);

  /** Paid & served event orders for the active profile — admin summary only. */
  const completedEventOrdersAdmin = useMemo(() => {
    const pid = activeEventProfile?.id;
    return ordersForEventUi
      .filter((o) => {
        if (o.orderType !== 'event') return false;
        if (o.paymentStatus !== 'paid') return false;
        if (o.status !== 'served') return false;
        return orderMatchesEventProfile(o, pid);
      })
      .sort((a, b) => orderCreatedAtMs(b) - orderCreatedAtMs(a));
  }, [ordersForEventUi, activeEventProfile?.id]);

  const completedEventGrossTotal = useMemo(
    () => completedEventOrdersAdmin.reduce((sum, o) => sum + (Number(o.totalAmount) || 0), 0),
    [completedEventOrdersAdmin],
  );

  const handleSendToKitchen = async () => {
    if (orderItems.length === 0) return;
    const effectiveOrderType: OrderType = isEventMode && eventModeAvailable ? 'event' : (orderType ?? 'takeaway');
    if (!isEventMode && !orderType) return;
    if ((effectiveOrderType === 'dine-in' || effectiveOrderType === 'buffet') && !selectedTable) {
      setShowTableSelect(true);
      return;
    }
    // Editing an existing unpaid event order
    if (isEventMode && eventModeAvailable && editingEventOrder) {
      setIsPlacing(true);
      try {
        const updatedSubtotal = orderItems.reduce((s, i) => s + i.price * i.quantity, 0);
        const updatedTax = updatedSubtotal * (15 / 115);
        let updatedOrder: Order = {
          ...editingEventOrder,
          items: orderItems,
          subtotal: updatedSubtotal,
          taxAmount: updatedTax,
          totalAmount: updatedSubtotal,
          notes: notes || undefined,
          eventFulfillmentType,
          pickupTime: isScheduledEventOrder && eventPickupTimeInput ? new Date(eventPickupTimeInput).getTime() : undefined,
          eventId: editingEventOrder.eventId ?? activeEventProfile?.id ?? editingEventOrder.eventMenuSnapshotId,
          eventNameSnapshot: editingEventOrder.eventNameSnapshot ?? activeEventProfile?.name ?? eventStall?.name,
          eventMenuSnapshotId: editingEventOrder.eventMenuSnapshotId ?? activeEventProfile?.id,
          eventCostSnapshot: orderItems.reduce<Record<string, number>>((acc, line) => {
            const id = Number(line.id);
            const cost = eventUnitCostByItemId.get(id);
            if (Number.isFinite(id) && cost != null) acc[String(id)] = cost;
            return acc;
          }, {}),
        };
        if (eventOpSession) {
          updatedOrder = {
            ...updatedOrder,
            eventOperatorId: eventOpSession.operatorId,
            eventOperatorName: eventOpSession.operatorName,
          };
        }
        await onUpdateOrder(updatedOrder);
        setEditingEventOrder(updatedOrder);
        setOrderItems(updatedOrder.items ?? []);
        setNotes(updatedOrder.notes || '');
        setShowCartSheet(false);
      } finally {
        setIsPlacing(false);
      }
      return;
    }

    setIsPlacing(true);
    try {
      const details: Parameters<typeof onPlaceOrder>[2] = {
        notes: notes || undefined,
        tableId: selectedTable != null ? Number(selectedTable.id) : undefined,
      };
      if (effectiveOrderType === 'event') {
        details.eventFulfillmentType = eventFulfillmentType;
        details.eventId = activeEventProfile?.id;
        details.eventNameSnapshot = activeEventProfile?.name ?? eventStall?.name;
        details.eventMenuSnapshotId = activeEventProfile?.id;
        details.eventCostSnapshot = orderItems.reduce<Record<string, number>>((acc, line) => {
          const id = Number(line.id);
          const cost = eventUnitCostByItemId.get(id);
          if (Number.isFinite(id) && cost != null) acc[String(id)] = cost;
          return acc;
        }, {});
        if (eventOpSession) {
          details.eventOperatorId = eventOpSession.operatorId;
          details.eventOperatorName = eventOpSession.operatorName;
        }
        if (isScheduledEventOrder && eventPickupTimeInput) {
          const pickupMs = new Date(eventPickupTimeInput).getTime();
          if (Number.isNaN(pickupMs) || pickupMs <= Date.now()) {
            alert('Please choose a valid future pickup time for scheduled orders.');
            setIsPlacing(false);
            return;
          }
          details.pickupTime = pickupMs;
        }
        details.eventQueueToken = nextEventTokenNumber;
      }
      if (effectiveOrderType === 'buffet') {
        const buffetStart = Date.now();
        const buffetEnd = buffetStart + 60 * 60 * 1000;
        (details as any).buffetType = 'lunch';
        (details as any).buffetStartTime = buffetStart;
        (details as any).buffetEndTime = buffetEnd;
      }
      if (effectiveOrderType === 'uber-doordash') {
        if (!uberDoordashDetails) {
          setShowUberDoordashModal(true);
          setIsPlacing(false);
          return;
        }
        details.deliveryInfo = {
          platform: uberDoordashDetails.platform,
          customerName: uberDoordashDetails.customerName,
        };
        details.customerName = uberDoordashDetails.customerName;
      }
      if (effectiveOrderType === 'delivery' && deliveryCustomerName.trim()) {
        details.customerName = deliveryCustomerName.trim();
      }
      const newOrder = await onPlaceOrder(orderItems, effectiveOrderType as OrderType, details);
      if (newOrder) {
        setPlacedOrder(newOrder);
        setShowCartSheet(false);
        if (effectiveOrderType === 'event') {
          setEventPaymentUiDismissed(false);
          setEditingEventOrder(newOrder);
          setOrderItems(newOrder.items ?? []);
          setNotes(newOrder.notes || '');
          setEventFulfillmentType(newOrder.eventFulfillmentType ?? 'eat-now');
          const pickupMs = normalizeEventPickupMs(newOrder.pickupTime);
          const scheduled = pickupMs != null && pickupMs > Date.now();
          setIsScheduledEventOrder(scheduled);
          setEventPickupTimeInput(
            typeof pickupMs === 'number' ? new Date(pickupMs).toISOString().slice(0, 16) : ''
          );
          setPaymentOrder(newOrder);
          setShowPaymentModal(true);
        } else {
          setOrderItems([]);
          setNotes('');
          setEventFulfillmentType('eat-now');
          setIsScheduledEventOrder(false);
          setEventPickupTimeInput('');
          if (waitstaffMode) {
            clearWaitstaffOrderDraft(waitstaffStaffId);
            setOrderToUpdate(null);
            setSelectedTable(null);
            setOrderType(null);
            setUberDoordashDetails(null);
            setDeliveryCustomerName('');
            setWaitstaffTab('home');
          }
        }
      }
    } finally {
      setIsPlacing(false);
    }
  };

  const handleAddToExistingOrder = async () => {
    if (!orderToUpdate || orderItems.length === 0) return;
    setIsPlacing(true);
    try {
      // Waitstaff loads the full ticket into the cart — cart is authoritative.
      // Non-waitstaff "add to existing" still merges new cart lines onto the ticket.
      let nextItems: OrderItem[];
      if (waitstaffMode) {
        nextItems = orderItems.map((item) => ({ ...item }));
      } else {
        const existingItems = orderToUpdate.items || [];
        const merged = [...existingItems];
        orderItems.forEach((cartItem) => {
          const match = merged.find(
            (m) => m.id === cartItem.id && (m as any).selectedMeatOption === (cartItem as any).selectedMeatOption
          );
          if (match) {
            (match as OrderItem).quantity += cartItem.quantity;
          } else {
            merged.push({ ...cartItem, status: 'pending' as const });
          }
        });
        nextItems = merged;
      }
      const newSubtotal = nextItems.reduce((s, i) => s + (i.removed ? 0 : i.price * i.quantity), 0);
      const newTax = newSubtotal * (15 / 115);
      await onUpdateOrder({
        ...orderToUpdate,
        items: nextItems,
        subtotal: newSubtotal,
        taxAmount: newTax,
        totalAmount: newSubtotal,
        notes: notes || orderToUpdate.notes,
      });
      setOrderToUpdate(null);
      setOrderItems([]);
      setNotes('');
      setShowOrderSelect(false);
      setShowCartSheet(false);
      if (waitstaffMode) {
        clearWaitstaffOrderDraft(waitstaffStaffId);
        setWaitstaffTab('home');
      }
    } finally {
      setIsPlacing(false);
    }
  };

  const loadOpenOrderForUpdate = (order: Order) => {
    const live = orders.find((o) => o.id === order.id) ?? order;
    const items = (live.items ?? [])
      .filter((i) => !i.removed)
      .map((i) => ({ ...i }));
    setOrderToUpdate(live);
    setOrderItems(items);
    setNotes(live.notes || '');
    const loadedType = (
      ['dine-in', 'takeaway', 'delivery', 'uber-doordash', 'buffet'] as const
    ).includes(live.orderType as OrderTypeOption)
      ? (live.orderType as OrderTypeOption)
      : 'dine-in';
    setOrderType(loadedType);
    if (live.orderType === 'uber-doordash' && live.deliveryInfo?.platform) {
      setUberDoordashDetails({
        platform: live.deliveryInfo.platform as UberDoordashPlatform,
        customerName: live.deliveryInfo.customerName || live.customerName || 'Customer',
      });
    }
    if (live.orderType === 'delivery') {
      setDeliveryCustomerName(live.customerName || '');
    }
    if (live.orderType === 'dine-in' && live.tableId != null) {
      const t = tables.find((tb) => Number(tb.id) === Number(live.tableId)) ?? null;
      setSelectedTable(t);
    } else {
      setSelectedTable(null);
    }
    setWaitstaffTab('order');
    setMobileViewMode('restaurant');
    setShowCartSheet(true);
  };

  const waitstaffStaffId = String(
    (currentUser as { id?: string | number; firestoreId?: string } | undefined)?.id
      ?? (currentUser as { firestoreId?: string } | undefined)?.firestoreId
      ?? 'anon',
  );

  const snapshotWaitstaffDraft = useCallback(
    () =>
      buildWaitstaffOrderDraft({
        staffId: waitstaffStaffId,
        items: orderItems,
        notes,
        orderType:
          orderType === 'dine-in' || orderType === 'takeaway' || orderType === 'delivery' || orderType === 'uber-doordash'
            ? (orderType as WaitstaffDraftOrderType)
            : null,
        table: selectedTable,
        deliveryCustomerName,
        uberDoordashDetails,
        orderToUpdateId: orderToUpdate?.id,
      }),
    [
      waitstaffStaffId,
      orderItems,
      notes,
      orderType,
      selectedTable,
      deliveryCustomerName,
      uberDoordashDetails,
      orderToUpdate?.id,
    ],
  );

  const liveWaitstaffDraft = snapshotWaitstaffDraft();
  const hasWaitstaffDraft = waitstaffMode && waitstaffDraftHasWork(liveWaitstaffDraft);

  useEffect(() => {
    if (!waitstaffMode) return;
    writeWaitstaffOrderDraft(snapshotWaitstaffDraft());
  }, [waitstaffMode, snapshotWaitstaffDraft]);

  useEffect(() => {
    if (!waitstaffMode) return;
    if (waitstaffDraftHydratedRef.current) return;
    if (!currentUser && waitstaffStaffId === 'anon') return;
    waitstaffDraftHydratedRef.current = true;
    const draft = readWaitstaffOrderDraft(waitstaffStaffId);
    if (!waitstaffDraftHasWork(draft) || !draft) return;
    setOrderItems(Array.isArray(draft.items) ? draft.items.map((item) => ({ ...item })) : []);
    setNotes(draft.notes || '');
    setOrderType(draft.orderType);
    setDeliveryCustomerName(draft.deliveryCustomerName || '');
    setUberDoordashDetails(draft.uberDoordashDetails ?? null);
    if (draft.tableId != null) {
      const table = tables.find((tb) => Number(tb.id) === Number(draft.tableId)) ?? null;
      setSelectedTable(table);
    } else {
      setSelectedTable(null);
    }
    if (draft.orderToUpdateId) {
      const live = orders.find((o) => o.id === draft.orderToUpdateId) ?? null;
      setOrderToUpdate(live);
    }
  }, [waitstaffMode, waitstaffStaffId, currentUser, tables, orders]);

  useEffect(() => {
    if (!waitstaffMode || orderToUpdate) return;
    const draft = readWaitstaffOrderDraft(waitstaffStaffId);
    if (!draft?.orderToUpdateId) return;
    const live = orders.find((o) => o.id === draft.orderToUpdateId);
    if (live) setOrderToUpdate(live);
  }, [waitstaffMode, waitstaffStaffId, orders, orderToUpdate]);

  const resetWaitstaffOrder = () => {
    setOrderItems([]);
    setNotes('');
    setOrderToUpdate(null);
    setSelectedTable(null);
    setOrderType(null);
    setPlacedOrder(null);
    setUberDoordashDetails(null);
    setDeliveryCustomerName('');
    setShowCartSheet(false);
    setWaitstaffTab('home');
  };

  const goWaitstaffHome = () => {
    writeWaitstaffOrderDraft(snapshotWaitstaffDraft());
    setShowCartSheet(false);
    setWaitstaffTab('home');
  };

  const resumeWaitstaffDraft = () => {
    setWaitstaffTab('order');
  };

  const discardWaitstaffDraft = () => {
    clearWaitstaffOrderDraft(waitstaffStaffId);
    resetWaitstaffOrder();
  };

  const beginWaitstaffNewOrder = (
    type: WaitstaffDraftOrderType,
    opts?: { pickTable?: boolean; openUber?: boolean },
  ) => {
    if (waitstaffDraftHasWork(snapshotWaitstaffDraft())) {
      const ok = window.confirm('You have an unfinished order. Discard it and start a new one?');
      if (!ok) {
        setWaitstaffTab('order');
        return;
      }
    }
    clearWaitstaffOrderDraft(waitstaffStaffId);
    setOrderItems([]);
    setNotes('');
    setOrderToUpdate(null);
    setSelectedTable(null);
    setPlacedOrder(null);
    setUberDoordashDetails(null);
    setDeliveryCustomerName('');
    setShowCartSheet(false);
    if (type === 'dine-in' || opts?.pickTable) {
      setOrderType('dine-in');
      setWaitstaffTab('order');
      setShowTableSelect(true);
      return;
    }
    setSelectedTable(null);
    setOrderType(type);
    if (opts?.openUber) setShowUberDoordashModal(true);
    setWaitstaffTab('order');
  };

  const handlePrint = (order?: Order) => {
    const o = order ?? placedOrder;
    if (!o) return;
    if (order) {
      setOrderToPrint(o);
      setTimeout(() => {
        const content = receiptRef.current;
        if (content) {
          const printWindow = window.open('', '_blank');
          if (printWindow) {
            printWindow.document.write(`
              <!DOCTYPE html><html><head><title>Receipt</title>
              <link rel="stylesheet" href="${window.location.origin}/index.css">
              <style>body{ padding:16px; font-family: system-ui,sans-serif; } .printable-receipt{ display:block !important; }</style>
              </head><body>
              ${content.innerHTML}
              </body></html>
            `);
            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => {
              printWindow.print();
              printWindow.close();
            }, 250);
          }
        }
        setOrderToPrint(null);
      }, 200);
      return;
    }
    const content = receiptRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print.');
      return;
    }
    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>Receipt</title>
      <link rel="stylesheet" href="${window.location.origin}/index.css">
      <style>body{ padding:16px; font-family: system-ui,sans-serif; } .printable-receipt{ display:block !important; }</style>
      </head><body>
      ${content.innerHTML}
      </body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const openPayment = () => {
    const target = placedOrder ?? editingEventOrder;
    if (target && target.paymentStatus === 'unpaid') {
      setPaymentOrder(target);
      setShowPaymentModal(true);
    }
  };

  const closePlacedOrder = () => {
    setPlacedOrder(null);
    setPaymentOrder(null);
    setShowPaymentModal(false);
    setEditingEventOrder(null);
    setOrderItems([]);
    setNotes('');
    setEventFulfillmentType('eat-now');
    setIsScheduledEventOrder(false);
    setEventPickupTimeInput('');
    setEventPaymentUiDismissed(true);
  };

  const handleCancelUnpaidEventOrder = async () => {
    const target = cancelTargetEventOrder;
    if (!target) return;
    if (
      !window.confirm(
        'Cancel this order? It will be marked cancelled in the system (same as voiding the ticket). You cannot undo this from the phone.'
      )
    ) {
      return;
    }
    try {
      await onUpdateOrder({
        ...target,
        status: 'cancelled',
      });
      setShowPaymentModal(false);
      setPaymentOrder(null);
      startNewEventOrder();
    } catch {
      alert('Failed to cancel order. Check your connection and try again.');
    }
  };

  const handleMarkEventOrderServed = async (order: Order) => {
    await onUpdateOrder({
      ...order,
      status: 'served',
      servedAt: Date.now(),
      items: (order.items ?? []).map((item) => ({ ...item, served: true })),
    });
  };

  const handleMarkEventOrderItemServed = async (order: Order, itemIndex: number, keepDetailOpen: boolean = false) => {
    const nextItems = (order.items ?? []).map((item, idx) => (
      idx === itemIndex ? { ...item, served: !item.served } : item
    ));
    const allServed = nextItems
      .filter((item) => !item.removed)
      .every((item) => item.served);

    const revertingLegacyServed = order.status === 'served' && !allServed;

    const updatedOrder: Order = {
      ...order,
      items: nextItems,
      ...(revertingLegacyServed
        ? { status: 'ready' as const, servedAt: undefined }
        : {}),
    };

    if (keepDetailOpen) {
      setServiceOrderDetail(updatedOrder);
    }
    setEventOrderItemPatch((prev) => ({ ...prev, [order.id]: nextItems }));

    try {
      await onUpdateOrder(updatedOrder);
      setEventOrderItemPatch((prev) => {
        const next = { ...prev };
        delete next[order.id];
        return next;
      });
    } catch {
      if (keepDetailOpen) {
        setServiceOrderDetail(order);
      }
      setEventOrderItemPatch((prev) => {
        const next = { ...prev };
        delete next[order.id];
        return next;
      });
      alert('Failed to update line. Check your connection and try again.');
    }
  };

  const openPaymentForOrder = (order: Order) => {
    setPaymentOrder(order);
    setShowPaymentModal(true);
  };

  useEffect(() => {
    if (!operatorsGateActive || unlockOperatorId || eventOperatorsConfigured.length === 0) return;
    setUnlockOperatorId(eventOperatorsConfigured[0].id);
  }, [operatorsGateActive, eventOperatorsConfigured, unlockOperatorId]);

  const tryUnlockEventOperators = async () => {
    const op = eventOperatorsConfigured.find((o) => o.id === unlockOperatorId);
    if (!op) {
      setUnlockError('Select an operator.');
      return;
    }
    const ok = await verifyEventOperatorPin(unlockPin, op.pinSalt, op.pinHash);
    if (!ok) {
      setUnlockError('Incorrect PIN.');
      return;
    }
    const sess = { operatorId: op.id, operatorName: op.name };
    writeEventOperatorSession(sess);
    setEventOpSession(sess);
    setUnlockPin('');
    setUnlockError(null);
  };

  const lockEventOperators = () => {
    clearEventOperatorSession();
    setEventOpSession(null);
    setUnlockPin('');
    setUnlockError(null);
  };

  /** Clear local draft so another customer/ticket can be started; unpaid Firestore orders stay in the list */
  const startNewEventOrder = () => {
    setEditingEventOrder(null);
    setOrderItems([]);
    setNotes('');
    setPlacedOrder(null);
    setPaymentOrder(null);
    setShowCartSheet(false);
    setShowPaymentModal(false);
    setEventPaymentUiDismissed(true);
    setEventFulfillmentType('eat-now');
    setIsScheduledEventOrder(false);
    setEventPickupTimeInput('');
  };

  if (!registerSession) {
    return (
      <RegisterClosedGuard
        onOpenRegister={onOpenRegister ?? (() => {})}
        currentUser={currentUser}
        isLoading={!registerSessionsHydrated}
        hideOpenButton={waitstaffMode}
      />
    );
  }

  if (operatorsGateActive && operatorGateBlocked) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-900/95 p-6 text-white">
        <h2 className="text-xl font-bold mb-2 text-center">Event Mobile POS</h2>
        <p className="text-sm text-gray-300 mb-6 text-center max-w-sm">Enter your operator PIN to continue.</p>
        <div className="w-full max-w-xs space-y-3">
          <label className="block text-xs font-medium text-gray-300">
            Operator
            <select
              value={unlockOperatorId}
              onChange={(e) => {
                setUnlockOperatorId(e.target.value);
                setUnlockError(null);
              }}
              className="mt-1 block w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-white text-sm"
            >
              {eventOperatorsConfigured.map((op) => (
                <option key={op.id} value={op.id}>
                  {op.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-medium text-gray-300">
            PIN
            <input
              type="password"
              value={unlockPin}
              onChange={(e) => {
                setUnlockPin(e.target.value);
                setUnlockError(null);
              }}
              className="mt-1 block w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-white text-sm"
              autoComplete="one-time-code"
            />
          </label>
          {unlockError ? <p className="text-sm text-red-400">{unlockError}</p> : null}
          <button
            type="button"
            onClick={() => void tryUnlockEventOperators()}
            className="w-full py-3 rounded-lg bg-pink-500 font-semibold text-white hover:bg-pink-600"
          >
            Unlock
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50 max-h-screen overflow-hidden">
      {!waitstaffMode && (
      <button
        type="button"
        onClick={toggleFullscreen}
        className="fixed top-3 left-3 z-[45] flex items-center justify-center w-11 h-11 rounded-full bg-gray-800 text-white shadow-lg hover:bg-gray-700"
        title={isFullscreen ? 'Exit maximized mode' : 'Maximize and hide side menu'}
      >
        {isFullscreen ? <ExitFullscreenIcon className="w-5 h-5" /> : <FullscreenIcon className="w-5 h-5" />}
      </button>
      )}
      {/* Sticky header - online order style */}
      <header className={`bg-white shadow-sm sticky top-0 z-30 flex-shrink-0 ${!waitstaffMode && eventModeAvailable ? 'pl-14' : ''}`}>
        {waitstaffMode && (
          <div className="px-3 pt-3 pb-2 space-y-2">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500">Floor staff</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{currentUser?.name || 'Staff'}</p>
              </div>
              {waitstaffTab === 'order' && (
                <button
                  type="button"
                  onClick={goWaitstaffHome}
                  className="text-xs font-semibold text-gray-600 px-2 py-1.5 rounded-lg bg-gray-100"
                >
                  Home
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowCartSheet(true)}
                className="relative flex items-center justify-center w-10 h-10 rounded-full bg-pink-500 text-white"
              >
                <ShoppingCartIcon className="w-5 h-5" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-5 flex items-center justify-center px-1">
                    {totalItems}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => onWaitstaffLock?.()}
                className="text-xs font-semibold text-pink-600 px-3 py-2 rounded-lg border border-pink-200"
              >
                Lock
              </button>
            </div>
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {([
                { id: 'home' as const, label: 'Order' },
                { id: 'pending' as const, label: `Pending (${waitstaffPendingOrders.length})` },
                { id: 'completed' as const, label: 'Done' },
                { id: 'kitchen' as const, label: 'Kitchen' },
              ]).map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    if (tab.id === 'home') goWaitstaffHome();
                    else setWaitstaffTab(tab.id);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                    waitstaffTab === tab.id
                      ? 'bg-pink-500 text-white'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {eventModeAvailable && operatorsGateActive && eventOpSession && !waitstaffMode && (
          <div className="px-3 pt-2 flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-gray-700 bg-gray-100 rounded-full px-2 py-1">
              {eventOpSession.operatorName}
            </span>
            <button
              type="button"
              onClick={lockEventOperators}
              className="text-xs font-medium text-pink-600 hover:text-pink-700"
            >
              Switch / Lock
            </button>
          </div>
        )}
        {pickupSoonLabel && eventModeAvailable && !waitstaffMode ? (
          <div className="mx-3 mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900">
            {pickupSoonLabel}
          </div>
        ) : null}
        {eventModeAvailable && !waitstaffMode && (
          <div className="px-3 py-2 flex items-center gap-2 overflow-x-auto flex-nowrap pb-1">
            <button
              type="button"
              onClick={() => {
                if (mobileViewMode === 'event' && eventModeAvailable) {
                  startNewEventOrder();
                }
                setMobileViewMode('event');
              }}
              className={`px-4 py-2 rounded-full text-sm font-semibold ${mobileViewMode === 'event' ? 'bg-pink-500 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              Order
            </button>
            <button
              type="button"
              onClick={() => setMobileViewMode('service')}
              className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1 ${
                mobileViewMode === 'service' ? 'bg-pink-500 text-white' : 'bg-gray-200 text-gray-700'
              }`}
            >
              <span>Service</span>
              {paidEventOrdersToServe.length > 0 && (
                <span className="text-[11px] font-semibold bg-amber-500 text-white rounded-full px-2 py-0.5">
                  {paidEventOrdersToServe.length}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setMobileViewMode('scheduled')}
              className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1 ${
                mobileViewMode === 'scheduled' ? 'bg-pink-500 text-white' : 'bg-gray-200 text-gray-700'
              }`}
            >
              <span>Scheduled</span>
              {scheduledEventOrders.length > 0 && (
                <span className="text-[11px] font-semibold bg-indigo-500 text-white rounded-full px-2 py-0.5">
                  {scheduledEventOrders.length}
                </span>
              )}
            </button>
            {eventAdminVisible && (
              <button
                type="button"
                onClick={() => setMobileViewMode('completed')}
                className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1 ${
                  mobileViewMode === 'completed' ? 'bg-slate-800 text-white' : 'bg-gray-200 text-gray-700'
                }`}
              >
                <span>Completed</span>
                {completedEventOrdersAdmin.length > 0 && (
                  <span className="text-[11px] font-semibold bg-slate-500 text-white rounded-full px-2 py-0.5">
                    {completedEventOrdersAdmin.length}
                  </span>
                )}
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowCartSheet(true)}
              className="relative ml-auto flex items-center justify-center w-10 h-10 rounded-full bg-pink-500 text-white hover:bg-pink-600 flex-shrink-0"
            >
              <ShoppingCartIcon className="w-5 h-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-5 flex items-center justify-center px-1">
                  {totalItems}
                </span>
              )}
            </button>
          </div>
        )}
        {(!eventModeAvailable || mobileViewMode !== 'event') &&
          mobileViewMode !== 'service' &&
          mobileViewMode !== 'scheduled' &&
          mobileViewMode !== 'completed' &&
          !(waitstaffMode && waitstaffTab !== 'order') && (
          <>
            {/* Order type */}
            <div className="px-3 pb-2 flex gap-2 overflow-x-auto">
              {(waitstaffMode
                ? (['dine-in', 'takeaway', 'delivery', 'uber-doordash'] as const)
                : (['dine-in', 'takeaway', 'delivery', 'buffet'] as const)
              ).map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    setOrderType(type);
                    if ((type === 'dine-in' || type === 'buffet') && !selectedTable) setShowTableSelect(true);
                    else if (type !== 'dine-in' && type !== 'buffet') setSelectedTable(null);
                    if (type === 'uber-doordash' && !uberDoordashDetails) setShowUberDoordashModal(true);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${
                    orderType === type ? 'bg-pink-500 text-white' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {type === 'dine-in' && 'Dine-in'}
                  {type === 'takeaway' && 'Takeaway'}
                  {type === 'delivery' && 'Delivery'}
                  {type === 'buffet' && 'Buffet'}
                  {type === 'uber-doordash' && 'Uber/DoorDash'}
                </button>
              ))}
            </div>
            {(orderType === 'dine-in' || orderType === 'buffet') && (
              <div className="px-3 pb-2 flex items-center gap-2 flex-wrap">
                {selectedTable ? (
                  <>
                    <span className="text-sm text-gray-600">Table: {selectedTable.name}</span>
                    <button type="button" onClick={() => setShowTableSelect(true)} className="text-sm text-pink-600 font-medium">
                      Change
                    </button>
                  </>
                ) : (
                  <button type="button" onClick={() => setShowTableSelect(true)} className="text-sm text-pink-600 font-medium">
                    Select table
                  </button>
                )}
                <button type="button" onClick={() => setShowTablesOverview(true)} className="text-sm text-gray-600 underline ml-auto">
                  View all tables
                </button>
              </div>
            )}
            {waitstaffMode && orderType === 'delivery' && !orderToUpdate && (
              <div className="px-3 pb-2">
                <input
                  type="text"
                  value={deliveryCustomerName}
                  onChange={(e) => setDeliveryCustomerName(e.target.value)}
                  placeholder="Delivery customer name (optional)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            )}
            {waitstaffMode && orderToUpdate && (
              <div className="px-3 pb-2">
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                  Running order · {waitstaffOrderTitle(orderToUpdate)}
                  {' · '}cart shows current items — add more, then Update order. Pay from Pending or the cart.
                </div>
              </div>
            )}
          </>
        )}
        {/* Category tabs - hidden in Service mode */}
        {!isServiceMode && !isScheduledMode && !isCompletedMode && !(waitstaffMode && waitstaffTab !== 'order') && (
        <>
        <div className="px-3 pb-2">
          <label className="relative block">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search menu to add…"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400"
            />
          </label>
        </div>
        <div className="border-t border-gray-100 px-2 py-2 flex gap-2 overflow-x-auto">
          {effectiveCategories.map((cat) => (
            <button
              key={cat.id}
              ref={(el) => {
                categoryChipRefs.current[String(cat.id)] = el;
              }}
              onClick={() => {
                if (isEventMode && eventModeAvailable) {
                  setSelectedCategoryId(selectedCategoryId === cat.id ? null : cat.id);
                  return;
                }
                scrollToCategory(cat.id);
              }}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${
                selectedCategoryId != null && String(selectedCategoryId) === String(cat.id)
                  ? 'bg-pink-500 text-white'
                  : 'bg-white text-gray-700 border border-gray-200'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
        </>
        )}
      </header>

      {waitstaffMode && waitstaffHome && (
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          <WaitstaffInstallHint />
          {hasWaitstaffDraft && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 space-y-3">
              <div>
                <p className="text-sm font-bold text-amber-950">Unfinished order</p>
                <p className="text-xs text-amber-900 mt-0.5">{waitstaffDraftSummary(liveWaitstaffDraft)}</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={resumeWaitstaffDraft}
                  className="flex-1 rounded-xl bg-pink-500 text-white py-3 text-sm font-bold"
                >
                  Continue order
                </button>
                <button
                  type="button"
                  onClick={discardWaitstaffDraft}
                  className="rounded-xl bg-white border border-amber-200 text-amber-900 px-3 py-3 text-xs font-semibold"
                >
                  Discard
                </button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => beginWaitstaffNewOrder('dine-in', { pickTable: true })}
              className="rounded-2xl bg-pink-500 text-white py-5 px-3 text-base font-bold shadow-sm active:scale-[0.99]"
            >
              New dine-in
            </button>
            <button
              type="button"
              onClick={() => beginWaitstaffNewOrder('takeaway')}
              className="rounded-2xl bg-gray-900 text-white py-5 px-3 text-base font-bold shadow-sm active:scale-[0.99]"
            >
              New takeaway
            </button>
            <button
              type="button"
              onClick={() => beginWaitstaffNewOrder('delivery')}
              className="rounded-2xl bg-white text-gray-900 py-5 px-3 text-base font-bold shadow-sm border border-gray-200 active:scale-[0.99]"
            >
              New delivery
            </button>
            <button
              type="button"
              onClick={() => beginWaitstaffNewOrder('uber-doordash', { openUber: true })}
              className="rounded-2xl bg-emerald-600 text-white py-5 px-3 text-base font-bold shadow-sm active:scale-[0.99]"
            >
              Uber / DoorDash
            </button>
          </div>
          <p className="text-xs text-gray-500">Use Pending to update tickets or take payment. Kitchen is the cook board.</p>
        </div>
      )}

      {waitstaffPending && (
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <h2 className="text-base font-bold text-gray-900">Pending ({waitstaffPendingOrders.length})</h2>
          {waitstaffPendingOrders.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center">
              <p className="text-sm text-gray-500">No pending orders.</p>
            </div>
          ) : (
            waitstaffPendingOrders.map((order) => (
              <WaitstaffTicketCard
                key={order.id}
                order={order}
                onOpen={() => loadOpenOrderForUpdate(order)}
                actions={
                  <>
                    <button
                      type="button"
                      onClick={() => loadOpenOrderForUpdate(order)}
                      className="flex-1 py-2 rounded-lg bg-gray-100 text-xs font-semibold text-gray-800"
                    >
                      Add items
                    </button>
                    {order.orderType !== 'uber-doordash' && (
                      <button
                        type="button"
                        onClick={() => openPaymentForOrder(order)}
                        className="flex-1 py-2 rounded-lg bg-pink-500 text-xs font-semibold text-white"
                      >
                        Pay
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setReceiptQrOrder(order)}
                      className="flex-1 py-2 rounded-lg bg-blue-50 text-xs font-semibold text-blue-700"
                    >
                      Receipt QR
                    </button>
                  </>
                }
              />
            ))
          )}
        </div>
      )}

      {waitstaffCompletedList && (
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <h2 className="text-base font-bold text-gray-900">Completed ({waitstaffCompletedOrders.length})</h2>
          {waitstaffCompletedOrders.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center">
              <p className="text-sm text-gray-500">No completed orders in the live list.</p>
            </div>
          ) : (
            waitstaffCompletedOrders.map((order) => (
              <WaitstaffTicketCard
                key={order.id}
                order={order}
                secondaryLabel={order.status}
                actions={
                  <button
                    type="button"
                    onClick={() => setReceiptQrOrder(order)}
                    className="flex-1 py-2 rounded-lg bg-blue-50 text-xs font-semibold text-blue-700"
                  >
                    Receipt QR
                  </button>
                }
              />
            ))
          )}
        </div>
      )}

      {waitstaffKitchen && onUpdateOrderItemStatus && (
        <div className="flex-1 min-h-0 overflow-hidden">
          <KitchenDisplayPage
            orders={orders}
            registerSession={registerSession as RegisterSession | undefined}
            registerSessionsHydrated={registerSessionsHydrated}
            currentUser={currentUser as CurrentUser | undefined}
            onUpdateOrderItemStatus={onUpdateOrderItemStatus}
            onStartOrder={onStartOrder}
            onRemoveOrder={onHideOrderFromKitchen}
            slaSettings={slaSettings}
            phoneLayout
          />
        </div>
      )}


      {/* Event mode: unpaid orders (pay upfront – use Payment or finish current order) */}
      {!(waitstaffMode && waitstaffHome) && isEventMode && eventModeAvailable && activeUnpaidEventOrders.length > 0 && (
        <div className="flex-shrink-0 border-b border-gray-200 bg-amber-50/80 px-3 py-2">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Unpaid event orders</h3>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {activeUnpaidEventOrders.map((order, index) => {
              const tokenLabel = getEventTokenDisplayLabel(order, eventTokenLabelById) ?? String(index + 1);
              const elapsedMs = Date.now() - orderCreatedAtMs(order);
              const elapsedMins = Math.floor(elapsedMs / 60000);
              const elapsedText = elapsedMins < 1 ? 'Just now' : elapsedMins === 1 ? '1 min ago' : `${elapsedMins} min ago`;
              return (
                <div key={order.id} className="flex-shrink-0 w-48 rounded-lg border border-amber-200 bg-white p-2 shadow-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">{eventTokenShortBadge(tokenLabel)}</span>
                    <span className="text-xs text-gray-500" title={new Date(order.timestamp).toLocaleTimeString()}>{elapsedText}</span>
                  </div>
                  <div className="text-sm font-medium text-gray-900 truncate mb-1">
                    {(order.items ?? []).reduce((sum, i) => sum + i.quantity, 0)} items · ${(order.totalAmount ?? 0).toFixed(2)}
                  </div>
                  <p className="text-xs text-gray-700 truncate mb-1">Token: {eventTokenShortBadge(tokenLabel)}</p>
                  <div className="flex flex-wrap gap-1">
                    <button type="button" onClick={() => { const pickupMs = typeof order.pickupTime === 'number' ? (order.pickupTime < 1_000_000_000_000 ? order.pickupTime * 1000 : order.pickupTime) : null; setMobileViewMode('event'); setEditingEventOrder(order); setOrderItems(order.items ?? []); setNotes(order.notes || ''); setEventFulfillmentType(order.eventFulfillmentType ?? 'eat-now'); setIsScheduledEventOrder(typeof pickupMs === 'number' && pickupMs > Date.now()); setEventPickupTimeInput(typeof pickupMs === 'number' ? new Date(pickupMs).toISOString().slice(0, 16) : ''); }} className="px-2 py-1 text-xs font-medium bg-gray-200 text-gray-800 rounded">Edit</button>
                    <button type="button" onClick={() => openPaymentForOrder(order)} className="px-2 py-1 text-xs font-medium bg-pink-500 text-white rounded">Payment</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Service mode: paid event orders to serve (list; click opens detail modal) */}
      {isServiceMode && eventModeAvailable && (
        <main className="flex-1 overflow-y-auto p-3 pb-24">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Orders to serve (oldest first)</h3>
          {paidEventOrdersToServe.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No orders waiting to be served.</p>
          ) : (
            <ul className="space-y-5">
              {paidEventOrdersToServe.map((order, index) => {
                const tokenLabel = getEventTokenDisplayLabel(order, eventTokenLabelById) ?? String(index + 1);
                const elapsedMs = Date.now() - orderCreatedAtMs(order);
                const elapsedMins = Math.floor(elapsedMs / 60000);
                const elapsedText = elapsedMins < 1 ? 'Just now' : elapsedMins === 1 ? '1 min ago' : `${elapsedMins} min ago`;
                const visibleItemsWithIndex = (order.items ?? [])
                  .map((item, originalIndex) => ({ item, originalIndex }))
                  .filter(({ item }) => !item.removed);
                const visibleItems = visibleItemsWithIndex.map(({ item }) => item);
                const hasMoreThanSixItems = visibleItems.length > 6;
                const cardItems = hasMoreThanSixItems ? visibleItemsWithIndex.slice(0, 6) : visibleItemsWithIndex;
                const totalOrderItems = (order.items ?? []).reduce((sum, i) => sum + i.quantity, 0);
                const orderTimeLabel = new Date(order.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                const allItemsReady = eventOrderAllItemsChecked(order);
                const fulfillmentLabel = order.eventFulfillmentType === 'takeaway' ? 'Take away' : 'Eat now';
                const pickupLabel = typeof order.pickupTime === 'number'
                  ? new Date(order.pickupTime < 1_000_000_000_000 ? order.pickupTime * 1000 : order.pickupTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  : null;
                return (
                  <li key={order.id}>
                    <div className="w-full text-left rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                      <div
                        className={`mb-3 -mx-5 -mt-5 px-4 h-14 rounded-t-xl border-b flex items-center justify-between gap-2 overflow-hidden ${
                          allItemsReady
                            ? 'bg-gradient-to-r from-emerald-500 to-green-600 border-emerald-400'
                            : 'bg-gradient-to-r from-amber-500 to-orange-500 border-amber-300'
                        }`}
                      >
                        <span className="inline-flex items-center rounded-md bg-white/40 text-black text-base font-extrabold px-3 py-1 whitespace-nowrap flex-shrink-0">
                          {formatEventTokenBanner(tokenLabel)}
                        </span>
                        <div className="flex flex-col items-end min-w-0 text-black">
                          <span className="text-[11px] font-bold uppercase tracking-wide leading-tight">
                            {fulfillmentLabel}
                          </span>
                          {pickupLabel && (
                            <span className="text-[11px] font-semibold leading-tight">
                              Pickup {pickupLabel}
                            </span>
                          )}
                          {allItemsReady && (
                            <span className="text-[11px] font-extrabold uppercase tracking-wide leading-tight">
                              Ready — confirm to complete
                            </span>
                          )}
                          <div className="flex items-center gap-2 whitespace-nowrap flex-shrink-0">
                            <span className="font-bold text-sm">{totalOrderItems} items</span>
                            <span className="font-extrabold text-sm">${(order.totalAmount ?? 0).toFixed(2)}</span>
                            <span className="font-semibold text-sm">{orderTimeLabel}</span>
                          </div>
                        </div>
                      </div>
                      <div className="min-w-0">
                          {cardItems.length > 0 && (
                            <div className="mt-2 flex flex-col gap-3">
                              {cardItems.map(({ item, originalIndex }, itemIndex) => (
                                <div
                                  key={`${order.id}-item-${itemIndex}`}
                                  className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 min-h-[4.25rem] touch-manipulation shadow-sm"
                                >
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={!!item.served}
                                    aria-label={`Mark ${item.name} complete`}
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      await handleMarkEventOrderItemServed(order, originalIndex, false);
                                    }}
                                    className={`flex-shrink-0 inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                                      item.served ? 'bg-emerald-500' : 'bg-gray-300'
                                    }`}
                                  >
                                    <span
                                      className={`h-7 w-7 rounded-full bg-white shadow transition-transform ${
                                        item.served ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                    />
                                  </button>
                                  <span
                                    className={`flex-1 min-w-0 text-base font-semibold leading-snug break-words ${
                                      item.served ? 'line-through text-gray-400' : 'text-gray-900'
                                    }`}
                                  >
                                    {item.name}
                                  </span>
                                  <span
                                    className={`flex-shrink-0 flex min-h-[3rem] min-w-[3rem] items-center justify-center rounded-xl border-2 px-2 text-center text-2xl font-black tabular-nums leading-none ${
                                      item.served
                                        ? 'border-gray-200 bg-gray-100 text-gray-500'
                                        : 'border-amber-400 bg-amber-100 text-amber-950'
                                    }`}
                                  >
                                    ×{item.quantity}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          {order.notes && (
                            <p className="text-sm text-amber-700 whitespace-pre-wrap break-words mt-1">
                              Note: {order.notes}
                            </p>
                          )}
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                        <p
                          className={`text-sm font-semibold ${
                            allItemsReady ? 'text-emerald-800' : 'text-amber-700'
                          }`}
                        >
                          {allItemsReady ? 'All items checked — hand off when ready' : `Waiting: ${elapsedText}`}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                        {!hasMoreThanSixItems && (
                          <button
                            type="button"
                            onClick={async () => {
                              await handleMarkEventOrderServed(order);
                            }}
                            className={`text-sm px-3 py-1.5 rounded text-white font-semibold shadow-sm ${
                              allItemsReady
                                ? 'bg-emerald-600 hover:bg-emerald-700'
                                : 'bg-amber-500 hover:bg-amber-600'
                            }`}
                          >
                            Complete
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setReceiptQrOrder(order)}
                          className="text-sm px-2.5 py-1.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                        >
                          QR
                        </button>
                        {hasMoreThanSixItems && (
                          <button
                            type="button"
                            onClick={() => setServiceOrderDetail(order)}
                            className={`text-sm px-3 py-1.5 rounded text-white font-semibold shadow-sm ${
                              allItemsReady
                                ? 'bg-emerald-600 hover:bg-emerald-700 ring-2 ring-emerald-200'
                                : 'bg-pink-500 hover:bg-pink-600 ring-2 ring-pink-200'
                            }`}
                          >
                            More
                          </button>
                        )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </main>
      )}

      {/* Scheduled mode: paid event orders not yet due for service */}
      {isScheduledMode && eventModeAvailable && (
        <main className="flex-1 overflow-y-auto p-3 pb-24">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Scheduled pickups (paid)</h3>
          <p className="text-xs text-gray-500 mb-2">These orders do not appear under Service until pickup time approaches; complete handoff here when ready.</p>
          {scheduledEventOrders.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No scheduled orders waiting.</p>
          ) : (
            <ul className="space-y-5">
              {scheduledEventOrders.map((order, index) => {
                const tokenLabel = getEventTokenDisplayLabel(order, eventTokenLabelById) ?? String(index + 1);
                const rawPickupMs = Number(order.pickupTime ?? 0);
                const pickupMs = rawPickupMs < 1_000_000_000_000 ? rawPickupMs * 1000 : rawPickupMs;
                const minsToPickup = Math.max(0, Math.ceil((pickupMs - nowMs) / 60000));
                const pickupLabel = Number.isFinite(pickupMs)
                  ? new Date(pickupMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  : 'Unknown';
                const schedAllChecked = eventOrderAllItemsChecked(order);
                const totalOrderItems = (order.items ?? []).reduce((sum, i) => sum + i.quantity, 0);
                const fulfillmentLabel = order.eventFulfillmentType === 'takeaway' ? 'Take away' : 'Eat now';
                const visibleItems = (order.items ?? []).filter((item) => !item.removed);
                const previewItems = visibleItems.slice(0, 5);
                const hasMoreItems = visibleItems.length > previewItems.length;
                return (
                  <li key={order.id}>
                    <div className="w-full rounded-xl border border-indigo-200 bg-white p-4 shadow-sm">
                      <div className="mb-3 -mx-4 -mt-4 px-4 h-14 rounded-t-xl bg-gradient-to-r from-indigo-500 to-violet-600 border-b border-indigo-300 flex items-center justify-between gap-2 overflow-hidden">
                        <span className="inline-flex items-center rounded-md bg-white/30 text-black text-base font-extrabold px-3 py-1 whitespace-nowrap">
                          {formatEventTokenBanner(tokenLabel)}
                        </span>
                        <div className="text-black text-right">
                          <p className="text-[11px] font-bold uppercase leading-tight">{fulfillmentLabel}</p>
                          <p className="text-sm font-semibold">{totalOrderItems} items · ${(order.totalAmount ?? 0).toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-indigo-800">Pickup: {pickupLabel}</p>
                        <p className="text-xs text-indigo-700">In ~{minsToPickup} min — you will hear a chime within 10 min of pickup.</p>
                        {previewItems.length > 0 && (
                          <div className="mt-3 flex flex-col gap-2.5">
                            {previewItems.map((item, itemIndex) => (
                              <div
                                key={`${order.id}-scheduled-item-${itemIndex}`}
                                className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5"
                              >
                                <span className="flex min-h-[2.75rem] min-w-[2.75rem] flex-shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-xl font-black tabular-nums text-indigo-950">
                                  ×{item.quantity}
                                </span>
                                <span className="min-w-0 flex-1 text-base font-semibold leading-snug text-gray-900">{item.name}</span>
                              </div>
                            ))}
                            {hasMoreItems && (
                              <p className="text-xs text-gray-500">+ {visibleItems.length - previewItems.length} more items</p>
                            )}
                          </div>
                        )}
                        {order.notes && <p className="text-sm text-amber-700 whitespace-pre-wrap break-words">Note: {order.notes}</p>}
                      </div>
                      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={async () => {
                            await handleMarkEventOrderServed(order);
                          }}
                          className={`text-sm px-3 py-1.5 rounded text-white font-semibold shadow-sm ${
                            schedAllChecked ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-500 hover:bg-amber-600'
                          }`}
                        >
                          Complete handoff
                        </button>
                        <button
                          type="button"
                          onClick={() => setServiceOrderDetail(order)}
                          className="text-sm px-2.5 py-1.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                        >
                          More
                        </button>
                        <button
                          type="button"
                          onClick={() => setReceiptQrOrder(order)}
                          className="text-sm px-2.5 py-1.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                        >
                          QR
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </main>
      )}

      {/* Admin: completed orders + gross sales (active event profile) */}
      {isCompletedMode && eventModeAvailable && eventAdminVisible && (
        <main className="flex-1 overflow-y-auto p-3 pb-24 flex flex-col gap-3">
          <div className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-white shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Event · Gross sales</p>
            <div className="mt-1 flex items-baseline justify-between gap-3">
              <p className="text-2xl font-bold tabular-nums">${completedEventGrossTotal.toFixed(2)}</p>
              <p className="text-xs text-slate-300">{completedEventOrdersAdmin.length} completed</p>
            </div>
            <p className="mt-1 text-[11px] text-slate-500">
              GST-inclusive totals · Paid and served · Active event profile only
            </p>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">Completed orders</h3>
          {completedEventOrdersAdmin.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No completed event orders yet.</p>
          ) : (
            <ul className="space-y-3">
              {completedEventOrdersAdmin.map((order, index) => {
                const tokenLabel = getEventTokenDisplayLabel(order, eventTokenLabelById) ?? String(index + 1);
                const servedRaw = order.servedAt;
                const doneMs =
                  typeof servedRaw === 'number' && Number.isFinite(servedRaw)
                    ? servedRaw < 1_000_000_000_000
                      ? servedRaw * 1000
                      : servedRaw
                    : orderCreatedAtMs(order);
                const doneLabel = new Date(doneMs).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
                const itemQty = (order.items ?? []).filter((i) => !i.removed).reduce((s, i) => s + i.quantity, 0);
                const fulfillmentLabel = order.eventFulfillmentType === 'takeaway' ? 'Take away' : 'Eat now';
                return (
                  <li key={order.id}>
                    <div className="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col overflow-hidden">
                      <div
                        role="button"
                        tabIndex={0}
                        className="p-4 flex flex-col gap-2 cursor-pointer hover:bg-slate-50 active:bg-slate-100/80 text-left outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                        onClick={() => setCompletedOrderDetail(order)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setCompletedOrderDetail(order);
                          }
                        }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="inline-flex items-center rounded-md bg-slate-100 text-slate-900 text-sm font-bold px-2.5 py-1">
                            {eventTokenShortBadge(tokenLabel)}
                          </span>
                          <span className="text-sm font-semibold text-gray-900 tabular-nums">
                            ${(order.totalAmount ?? 0).toFixed(2)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600">
                          {itemQty} items · {fulfillmentLabel}
                        </p>
                        <p className="text-[11px] text-gray-500">Completed {doneLabel}</p>
                        <p className="text-[11px] text-slate-500 font-medium">Tap for details</p>
                      </div>
                      <div className="px-4 py-2 flex justify-end border-t border-gray-100 bg-gray-50/80">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setReceiptQrOrder(order);
                          }}
                          className="text-xs px-2.5 py-1.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium"
                        >
                          QR receipt
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </main>
      )}

      {/* Menu grid - when not in Service mode */}
      {!isServiceMode && !isScheduledMode && !isCompletedMode && !(waitstaffMode && waitstaffTab !== 'order') && (
      <main ref={menuScrollRef} className="flex-1 overflow-y-auto p-3 pb-24">
        {isEventMode && eventModeAvailable && !eventMenuConfigured && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Event mode is enabled, but no valid Event menu items are configured. Add items with required prices in Settings → Event/Stall (Mobile POS).
          </div>
        )}
        {searchQuery.trim() ? (
          <div className="mb-3 rounded-xl border border-gray-200 bg-white p-3">
            <p className="text-xs font-semibold text-gray-600 mb-2">Add custom item</p>
            <div className="flex gap-2">
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={customAddPrice}
                onChange={(e) => setCustomAddPrice(e.target.value)}
                placeholder="Price"
                className="w-24 rounded-lg border border-gray-300 px-2 py-2 text-sm"
              />
              <button
                type="button"
                onClick={addCustomFromSearch}
                className="flex-1 rounded-lg bg-gray-900 text-white text-sm font-semibold py-2"
              >
                Add “{searchQuery.trim()}”
              </button>
            </div>
          </div>
        ) : null}
        {menuSections.length === 0 ? (
          <p className="text-sm text-center text-gray-500 py-10">No matching menu items.</p>
        ) : null}
        <div
          className={
            isEventMode && eventModeAvailable
              ? eventMenuGroupedSections
                ? 'space-y-6'
                : 'space-y-3'
              : 'space-y-6'
          }
        >
          {menuSections.map((section) => (
            <div
              key={section.categoryId || section.categoryLabel || '_all'}
              className={section.categoryLabel ? 'space-y-3 scroll-mt-2' : ''}
            >
              {section.categoryLabel ? (
                <h3
                  ref={(el) => {
                    const key = section.categoryId || section.categoryLabel;
                    if (key) categorySectionRefs.current[key] = el;
                  }}
                  data-category-id={section.categoryId || section.categoryLabel}
                  className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-0.5 pt-1 border-b border-gray-100 pb-1.5"
                >
                  {section.categoryLabel}
                </h3>
              ) : null}
              <div className={isEventMode && eventModeAvailable ? 'space-y-3' : 'space-y-2'}>
                {section.items.map((item) => {
                  const lineQty = cartQuantityForMenuItem(item);
                  const inCart = lineQty > 0;
                  const availableUnits = getEventAvailableUnits(Number(item.id));
                  const stallSoldOut = !!(item as MenuItem & { stallSoldOut?: boolean }).stallSoldOut;
                  const soldOut = stallSoldOut || (availableUnits != null && lineQty >= availableUnits);
                  const eventMenuLarge = isEventMode && eventModeAvailable;
                  const nameWrap = eventMenuLarge ? 'break-words whitespace-normal' : 'truncate';
                  const descWrap = eventMenuLarge ? 'break-words whitespace-normal' : 'truncate';
                  return (
                    <div
                      key={item.id}
                      className={`w-full rounded-lg shadow-sm flex items-stretch hover:shadow-md transition-colors ${
                        stallSoldOut
                          ? 'bg-gray-100 opacity-70 border border-gray-200'
                          : inCart
                            ? 'bg-pink-50 ring-2 ring-pink-300 border border-pink-200'
                            : 'bg-white border border-transparent'
                      }`}
                    >
                      <button
                        type="button"
                        disabled={stallSoldOut}
                        onClick={() => addToCart(item)}
                        className={`flex-1 min-w-0 px-3 text-left active:scale-[0.99] transition-transform disabled:opacity-50 disabled:pointer-events-none self-start ${
                          eventMenuLarge ? 'py-3' : 'py-2'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p
                              className={`font-semibold ${nameWrap} ${
                                eventMenuLarge ? 'text-base' : 'text-sm'
                              } ${inCart ? 'text-pink-950' : 'text-gray-800'}`}
                            >
                              {item.name}
                            </p>
                            {item.description ? (
                              <p
                                className={`${descWrap} ${eventMenuLarge ? 'text-sm' : 'text-xs'} ${
                                  inCart ? 'text-pink-800/80' : 'text-gray-500'
                                }`}
                              >
                                {item.description}
                              </p>
                            ) : null}
                            {isEventMode && (
                              <p
                                className={`mt-0.5 font-semibold ${
                                  eventMenuLarge ? 'text-sm' : 'text-[11px]'
                                } ${
                                  stallSoldOut
                                    ? 'text-gray-600'
                                    : availableUnits == null
                                      ? 'text-indigo-700'
                                      : availableUnits > 0
                                        ? 'text-indigo-700'
                                        : 'text-red-600'
                                }`}
                              >
                                {stallSoldOut
                                  ? 'Sold out'
                                  : availableUnits == null
                                    ? 'Balance: Unlimited'
                                    : `Balance: ${Math.max(0, availableUnits - lineQty)}`}
                              </p>
                            )}
                          </div>
                          <span
                            className={`text-pink-600 font-bold flex-shrink-0 self-start ${
                              eventMenuLarge ? 'text-lg' : 'text-sm'
                            }`}
                          >
                            {item.soldByWeight ? 'By weight' : `$${item.price.toFixed(2)}`}
                          </span>
                        </div>
                      </button>
                      {!item.soldByWeight && (
                      <div
                        className={`flex items-center gap-1 pr-2 pl-1 border-l flex-shrink-0 self-stretch ${
                          eventMenuLarge ? 'py-3' : 'py-2'
                        } ${inCart ? 'border-pink-200 bg-pink-100/80' : 'border-gray-100 bg-gray-50/80'}`}
                      >
                        <button
                          type="button"
                          aria-label={`Decrease ${item.name}`}
                          disabled={lineQty <= 0}
                          onClick={(e) => {
                            e.preventDefault();
                            decrementMenuItemFromCart(item);
                          }}
                          className={`rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold disabled:opacity-40 disabled:pointer-events-none active:bg-gray-300 ${
                            eventMenuLarge ? 'w-10 h-10 text-lg' : 'w-8 h-8'
                          }`}
                        >
                          −
                        </button>
                        <span
                          className={`min-w-[1.25rem] text-center font-semibold text-gray-900 tabular-nums ${
                            eventMenuLarge ? 'text-base' : 'text-sm'
                          }`}
                        >
                          {lineQty}
                        </span>
                        <button
                          type="button"
                          aria-label={`Increase ${item.name}`}
                          disabled={soldOut}
                          onClick={(e) => {
                            e.preventDefault();
                            addToCart(item);
                          }}
                          className={`rounded-full bg-pink-500 flex items-center justify-center text-white font-bold active:bg-pink-600 disabled:opacity-40 disabled:pointer-events-none ${
                            eventMenuLarge ? 'w-10 h-10 text-lg' : 'w-8 h-8'
                          }`}
                        >
                          +
                        </button>
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </main>
      )}

      {/* Cart bottom sheet */}
      {showCartSheet && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowCartSheet(false)}>
          <div
            className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[88vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold">Cart ({totalItems})</h2>
              <button type="button" onClick={() => setShowCartSheet(false)} className="p-2 text-gray-500 hover:text-gray-700">
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {orderItems.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Cart is empty</p>
              ) : (
                <>
                  {orderItems.map((item, idx) => {
                    const fishDetail = formatLiveFishDetail(item);
                    return (
                    <div key={idx} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg p-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{item.name}</p>
                        {fishDetail && (
                          <p className="text-xs text-gray-600">{fishDetail}</p>
                        )}
                        <p className="text-sm text-pink-600">${(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!isWeighedFishLine(item) && (
                          <>
                        <button type="button" onClick={() => updateQuantity(idx, -1)} className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold">−</button>
                        <span className="w-8 text-center font-semibold">{item.quantity}</span>
                        <button type="button" onClick={() => updateQuantity(idx, 1)} className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold">+</button>
                          </>
                        )}
                        <button type="button" onClick={() => removeFromCart(idx)} className="p-1 text-red-500">
                          <XCircleIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    );
                  })}
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">Notes</span>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Special instructions..."
                      className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      rows={2}
                    />
                  </label>
                {isEventMode && (
                  <div className="rounded-lg border border-pink-200 bg-pink-50 px-3 py-2">
                    <p className="text-sm font-semibold text-pink-800 mb-2">Order type</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setEventFulfillmentType('eat-now')}
                        className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                          eventFulfillmentType === 'eat-now'
                            ? 'bg-pink-500 text-white'
                            : 'bg-white text-gray-700 border border-gray-200'
                        }`}
                      >
                        Eat now
                      </button>
                      <button
                        type="button"
                        onClick={() => setEventFulfillmentType('takeaway')}
                        className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                          eventFulfillmentType === 'takeaway'
                            ? 'bg-pink-500 text-white'
                            : 'bg-white text-gray-700 border border-gray-200'
                        }`}
                      >
                        Take away
                      </button>
                    </div>
                  </div>
                )}
                {isEventMode && (
                  <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
                    <p className="text-sm font-semibold text-indigo-800 mb-2">Serve time</p>
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => {
                          setIsScheduledEventOrder(false);
                          setEventPickupTimeInput('');
                        }}
                        className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                          !isScheduledEventOrder
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-200'
                        }`}
                      >
                        ASAP
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsScheduledEventOrder(true)}
                        className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                          isScheduledEventOrder
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-200'
                        }`}
                      >
                        Schedule later
                      </button>
                    </div>
                    {isScheduledEventOrder && (
                      <input
                        type="datetime-local"
                        value={eventPickupTimeInput}
                        min={new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16)}
                        onChange={(e) => setEventPickupTimeInput(e.target.value)}
                        className="w-full rounded-md border border-indigo-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      />
                    )}
                    <p className="text-xs text-indigo-700 mt-2">
                      Scheduled orders stay on the Scheduled tab (not Service). You will get an alert within 10 minutes of pickup.
                    </p>
                  </div>
                )}
                  {/* Add to existing order - restaurant dine-in / takeaway */}
                  {!isEventMode && !waitstaffMode && (orderType === 'dine-in' || orderType === 'takeaway') && (
                    (orderType === 'dine-in' ? activeUnpaidDineIn : activeUnpaidTakeaway).length > 0
                  ) && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Or add to existing order</p>
                      <select
                        value={orderToUpdate?.id ?? ''}
                        onChange={(e) => {
                          const id = e.target.value;
                          const pool = orderType === 'takeaway' ? activeUnpaidTakeaway : activeUnpaidDineIn;
                          setOrderToUpdate(id ? pool.find((o) => o.id === id) ?? null : null);
                        }}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      >
                        <option value="">New order</option>
                        {(orderType === 'takeaway' ? activeUnpaidTakeaway : activeUnpaidDineIn).map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.orderType === 'takeaway'
                              ? `Takeaway ${o.customerNumber != null ? `#${o.customerNumber}` : o.id}`
                              : `${o.tableName || `Table ${o.tableId}`}`}
                            {' – '}${o.totalAmount.toFixed(2)}
                          </option>
                        ))}
                      </select>
                      {orderToUpdate && (
                        <button
                          type="button"
                          onClick={handleAddToExistingOrder}
                          className="w-full py-3 bg-amber-500 text-white font-semibold rounded-lg"
                        >
                          Add to this order
                        </button>
                      )}
                    </div>
                  )}
                  {waitstaffMode && orderToUpdate && orderItems.length > 0 && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      Use <span className="font-semibold">Update order</span> below to send changes to kitchen.
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-semibold">${total.toFixed(2)}</span>
              </div>
              {isEventMode &&
                editingEventOrder &&
                editingEventOrder.paymentStatus === 'unpaid' &&
                orderItems.length === 0 &&
                eventModeAvailable && (
                  <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    Removing all items does not cancel the ticket. Use <span className="font-semibold">Cancel entire order</span> below to void it.
                  </p>
                )}
              {waitstaffMode && orderToUpdate && orderItems.length > 0 && (
                <button
                  type="button"
                  onClick={handleAddToExistingOrder}
                  disabled={isPlacing}
                  className="w-full py-3 bg-amber-500 text-white font-semibold rounded-lg disabled:opacity-50"
                >
                  {isPlacing ? 'Updating…' : 'Update order'}
                </button>
              )}
              {waitstaffMode && orderToUpdate && orderToUpdate.orderType !== 'uber-doordash' && (
                <button
                  type="button"
                  onClick={() => {
                    setShowCartSheet(false);
                    openPaymentForOrder(orderToUpdate);
                  }}
                  className="w-full py-3 bg-pink-500 text-white font-semibold rounded-lg"
                >
                  Pay
                </button>
              )}
              {waitstaffMode && orderToUpdate && (
                <button
                  type="button"
                  onClick={() => {
                    setShowCartSheet(false);
                    setReceiptQrOrder(orderToUpdate);
                  }}
                  className="w-full py-2.5 bg-blue-50 text-blue-700 font-semibold rounded-lg"
                >
                  Receipt QR
                </button>
              )}
              {orderItems.length > 0 && !orderToUpdate && (
                <>
                  {isEventMode &&
                    editingEventOrder &&
                    editingEventOrder.paymentStatus === 'unpaid' &&
                    eventModeAvailable && (
                      <button
                        type="button"
                        onClick={() => {
                          setPaymentOrder(editingEventOrder);
                          setShowPaymentModal(true);
                          setShowCartSheet(false);
                        }}
                        className="w-full py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700"
                      >
                        Pay now
                      </button>
                    )}
                  <button
                    type="button"
                    onClick={handleSendToKitchen}
                    disabled={isPlacing || (!isEventMode && !orderType)}
                    className="w-full py-3 bg-pink-500 text-white font-semibold rounded-lg disabled:opacity-50"
                  >
                    {isPlacing
                      ? (isEventMode ? 'Placing…' : 'Sending…')
                      : isEventMode
                        ? editingEventOrder
                          ? 'Update order'
                          : 'Place order'
                        : 'Send to kitchen'}
                  </button>
                </>
              )}
              {isEventMode &&
                editingEventOrder &&
                editingEventOrder.paymentStatus === 'unpaid' &&
                eventModeAvailable &&
                cancelTargetEventOrder && (
                  <button
                    type="button"
                    onClick={() => void handleCancelUnpaidEventOrder()}
                    className="w-full py-2.5 text-sm font-semibold text-red-800 border-2 border-red-300 rounded-lg hover:bg-red-50"
                  >
                    Cancel entire order
                  </button>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Tables overview – all tables and statuses */}
      {showTablesOverview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowTablesOverview(false)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">Tables &amp; status</h3>
              <button type="button" onClick={() => setShowTablesOverview(false)} className="text-gray-500 p-1">✕</button>
            </div>
            <div className="overflow-y-auto flex-1 p-3">
              {[...tables]
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
                .map((t) => {
                  const tableOrder = orders.find(
                    (o) => orderMatchesFloorPlanTable(o, t.id) && o.orderType !== 'online' && o.paymentStatus === 'unpaid' && o.status !== 'cancelled' && o.status !== 'refunded'
                  );
                  const statusLabel = t.status === 'available' ? 'Available' : t.status === 'occupied' ? 'Occupied' : 'Reserved';
                  const statusClass = t.status === 'available' ? 'bg-green-100 text-green-800' : t.status === 'occupied' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700';
                  return (
                    <div key={t.id} className="flex items-center justify-between gap-2 py-3 px-3 rounded-lg bg-gray-50 mb-2">
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold text-gray-900">{t.name}</span>
                        <span className="text-gray-500 text-sm ml-2">({t.capacity} seats)</span>
                      </div>
                      <span className={`text-xs font-medium px-2 py-1 rounded ${statusClass}`}>{statusLabel}</span>
                      {tableOrder && (
                        <span className="text-sm text-gray-600 truncate">${tableOrder.totalAmount?.toFixed(0) ?? '0'}</span>
                      )}
                    </div>
                  );
                })}
            </div>
            <div className="p-3 border-t bg-gray-50">
              <button type="button" onClick={() => { setShowTablesOverview(false); setShowTableSelect(true); }} className="w-full py-2 text-sm font-medium text-pink-600">
                Select a table for order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table select modal */}
      {showTableSelect && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowTableSelect(false)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[70vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b">
              <h3 className="text-lg font-bold">Select table</h3>
            </div>
            <div className="overflow-y-auto max-h-60 p-2">
              {tables
                .filter((t) => t.status === 'available' || t.id === selectedTable?.id)
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
                .map((t) => {
                  const statusLabel = t.status === 'available' ? 'Available' : t.status === 'occupied' ? 'Occupied' : 'Reserved';
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => {
                        setSelectedTable(t);
                        setShowTableSelect(false);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg mb-1 ${
                        selectedTable?.id === t.id ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {t.name} (seats {t.capacity}) · {statusLabel}
                    </button>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* Service: order detail modal (items + Mark as served) */}
      {serviceOrderDetail && (() => {
        const detailAllChecked = eventOrderAllItemsChecked(serviceOrderDetail);
        return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setServiceOrderDetail(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div
              className={`p-4 border-b flex justify-between items-center ${
                detailAllChecked ? 'bg-emerald-50 border-emerald-200' : ''
              }`}
            >
              <h3 className="text-lg font-bold">Order · ${(serviceOrderDetail.totalAmount ?? 0).toFixed(2)}</h3>
              <button type="button" onClick={() => setServiceOrderDetail(null)} className="p-2 text-gray-500 hover:text-gray-700">&#215;</button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <p className="text-xs text-gray-500 mb-3">{new Date(serviceOrderDetail.timestamp).toLocaleTimeString()}</p>
              {typeof serviceOrderDetail.pickupTime === 'number' && (
                <p className="text-xs text-indigo-700 font-semibold mb-3">
                  Pickup at {new Date(serviceOrderDetail.pickupTime < 1_000_000_000_000 ? serviceOrderDetail.pickupTime * 1000 : serviceOrderDetail.pickupTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
              <ul className="flex flex-col gap-3">
                {(serviceOrderDetail.items ?? [])
                  .map((item, idx) => ({ item, idx }))
                  .filter(({ item }) => !item.removed)
                  .map(({ item, idx }) => (
                  <li
                    key={idx}
                    className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 min-h-[4.5rem]"
                  >
                    <span
                      className={`flex min-h-[3.25rem] min-w-[3.25rem] flex-shrink-0 items-center justify-center rounded-xl border-2 px-2 text-2xl font-black tabular-nums ${
                        item.served ? 'border-gray-200 bg-gray-100 text-gray-500' : 'border-pink-300 bg-pink-50 text-pink-950'
                      }`}
                    >
                      ×{item.quantity}
                    </span>
                    <div className="min-w-0 flex-1 basis-[min(100%,12rem)]">
                      <span className={`text-base font-semibold leading-snug ${item.served ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                        {item.name}
                      </span>
                      {item.served && (
                        <p className="text-sm text-green-700 font-semibold mt-1">Completed</p>
                      )}
                      <p className="text-sm text-pink-600 font-semibold mt-0.5">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                    <div className="ml-auto flex flex-shrink-0 items-center gap-2">
                      <span className="sr-only">Complete line</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!!item.served}
                        aria-label={`Toggle ${item.name} complete`}
                        onClick={async () => {
                          await handleMarkEventOrderItemServed(serviceOrderDetail, idx, true);
                        }}
                        className={`inline-flex h-8 w-14 flex-shrink-0 items-center rounded-full transition-colors ${
                          item.served ? 'bg-emerald-500' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`h-7 w-7 rounded-full bg-white shadow transition-transform ${
                            item.served ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 border-t bg-gray-50">
              {detailAllChecked && (
                <p className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900 font-semibold text-center">
                  All items checked — tap Mark as served when handed off
                </p>
              )}
              <button
                type="button"
                onClick={() => setReceiptQrOrder(serviceOrderDetail)}
                className="w-full py-2 mb-2 bg-blue-50 text-blue-700 font-semibold rounded-lg hover:bg-blue-100"
              >
                Show customer receipt QR
              </button>
              <button
                type="button"
                onClick={async () => {
                  await handleMarkEventOrderServed(serviceOrderDetail);
                  setServiceOrderDetail(null);
                }}
                className={`w-full py-3 text-white font-semibold rounded-lg ${
                  detailAllChecked ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-500 hover:bg-amber-600'
                }`}
              >
                Mark as served
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Admin completed: read-only order detail modal */}
      {completedOrderDetail && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setCompletedOrderDetail(null)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[88vh] overflow-hidden flex flex-col shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-start gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Completed order</p>
                <p className="text-lg font-bold text-gray-900 mt-0.5 tabular-nums">
                  ${(completedOrderDetail.totalAmount ?? 0).toFixed(2)}
                </p>
                <p className="text-sm font-semibold text-slate-800 mt-1">
                  {formatEventTokenBanner(
                    getEventTokenDisplayLabel(completedOrderDetail, eventTokenLabelById) ?? '?',
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCompletedOrderDetail(null)}
                className="p-2 text-gray-500 hover:text-gray-700 shrink-0 rounded-lg hover:bg-gray-100"
                aria-label="Close"
              >
                &#215;
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 space-y-4">
              <dl className="text-xs text-gray-600 space-y-1.5">
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">Placed</dt>
                  <dd className="font-medium text-gray-800 text-right">
                    {new Date(orderCreatedAtMs(completedOrderDetail)).toLocaleString([], {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">Completed</dt>
                  <dd className="font-medium text-gray-800 text-right">
                    {(() => {
                      const raw = completedOrderDetail.servedAt;
                      const ms =
                        typeof raw === 'number' && Number.isFinite(raw)
                          ? raw < 1_000_000_000_000
                            ? raw * 1000
                            : raw
                          : orderCreatedAtMs(completedOrderDetail);
                      return new Date(ms).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
                    })()}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">Type</dt>
                  <dd className="font-medium text-gray-800 text-right">
                    {completedOrderDetail.eventFulfillmentType === 'takeaway' ? 'Take away' : 'Eat now'}
                  </dd>
                </div>
                {completedOrderDetail.eventOperatorName ? (
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-500">Operator</dt>
                    <dd className="font-medium text-gray-800 text-right truncate max-w-[60%]">
                      {completedOrderDetail.eventOperatorName}
                    </dd>
                  </div>
                ) : null}
                {typeof completedOrderDetail.pickupTime === 'number' ? (
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-500">Pickup</dt>
                    <dd className="font-medium text-gray-800 text-right">
                      {new Date(
                        completedOrderDetail.pickupTime < 1_000_000_000_000
                          ? completedOrderDetail.pickupTime * 1000
                          : completedOrderDetail.pickupTime,
                      ).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                    </dd>
                  </div>
                ) : null}
              </dl>
              {completedOrderDetail.notes ? (
                <p className="text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 whitespace-pre-wrap break-words">
                  <span className="font-semibold">Notes: </span>
                  {completedOrderDetail.notes}
                </p>
              ) : null}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Items</p>
                <ul className="flex flex-col gap-2">
                  {(completedOrderDetail.items ?? [])
                    .filter((line) => !line.removed)
                    .map((item, idx) => (
                      <li
                        key={`${completedOrderDetail.id}-line-${idx}`}
                        className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5"
                      >
                        <span className="flex min-h-9 min-w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white border border-gray-200 text-base font-bold text-gray-900">
                          ×{item.quantity}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900 leading-snug">{item.name}</p>
                          <p className="text-sm text-pink-600 font-semibold mt-0.5 tabular-nums">
                            ${(item.price * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
              <div className="border-t border-gray-200 pt-3 text-sm space-y-1 text-gray-700">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span className="tabular-nums">${(completedOrderDetail.subtotal ?? 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>GST</span>
                  <span className="tabular-nums">${(completedOrderDetail.taxAmount ?? 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-gray-900 pt-1 border-t border-gray-100">
                  <span>Total</span>
                  <span className="tabular-nums">${(completedOrderDetail.totalAmount ?? 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50">
              <button
                type="button"
                onClick={() => {
                  const o = completedOrderDetail;
                  setCompletedOrderDetail(null);
                  setReceiptQrOrder(o);
                }}
                className="w-full py-2.5 bg-blue-50 text-blue-700 font-semibold rounded-lg hover:bg-blue-100"
              >
                Show customer receipt QR
              </button>
            </div>
          </div>
        </div>
      )}

      {/* After place: non-event keeps Print; event uses Payment / Done only (no printer on stalls) */}
      {placedOrder &&
        placedOrder.paymentStatus === 'unpaid' &&
        !showPaymentModal &&
        eventPaymentUiDismissed &&
        !waitstaffMode && (
        <div className="fixed bottom-0 left-0 right-0 z-30 p-4 bg-white border-t shadow-lg flex flex-wrap gap-3">
          {placedOrder.orderType !== 'event' && (
            <button type="button" onClick={() => handlePrint()} className="flex-1 py-3 bg-gray-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2">
              <PrinterIcon className="w-5 h-5" />
              Print
            </button>
          )}
          {placedOrder.orderType === 'event' && cancelTargetEventOrder && (
            <button
              type="button"
              onClick={() => void handleCancelUnpaidEventOrder()}
              className="flex-1 min-w-[120px] py-3 border-2 border-red-400 text-red-800 font-semibold rounded-lg hover:bg-red-50"
            >
              Cancel order
            </button>
          )}
          <button type="button" onClick={openPayment} className="flex-1 min-w-[120px] py-3 bg-pink-500 text-white font-semibold rounded-lg flex items-center justify-center gap-2">
            <CreditCardIcon className="w-5 h-5" />
            Payment
          </button>
          <button
            type="button"
            onClick={closePlacedOrder}
            className={`py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg ${
              placedOrder.orderType === 'event' ? 'flex-1 min-w-[120px] px-4' : 'px-4 flex-shrink-0'
            }`}
          >
            Done
          </button>
        </div>
      )}

      {showPaymentModal && resolvedPaymentOrder && (
        <PaymentModal
          order={resolvedPaymentOrder}
          customer={resolvedPaymentOrder.customerId ? customers.find((c) => c.id === resolvedPaymentOrder.customerId) ?? null : null}
          terminals={paymentModalTerminals.list}
          terminalSelectionNotice={paymentModalTerminals.notice}
          eventTokenDisplay={
            resolvedPaymentOrder.orderType === 'event'
              ? formatEventTokenBanner(getEventTokenDisplayLabel(resolvedPaymentOrder, eventTokenLabelById) ?? '') || undefined
              : undefined
          }
          onCancelUnpaidOrder={
            resolvedPaymentOrder.orderType === 'event' && resolvedPaymentOrder.paymentStatus === 'unpaid'
              ? handleCancelUnpaidEventOrder
              : undefined
          }
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentOrder(null);
            setEventPaymentUiDismissed(true);
          }}
          onProcessPayment={onProcessPayment}
          onPrintReceipt={(order) => {
            if (placedOrder?.id === order.id) setPlacedOrder(order);
            setTimeout(() => handlePrint(order), 100);
          }}
          onNewOrder={() => {
            closePlacedOrder();
            if (waitstaffMode) setWaitstaffTab('home');
          }}
          onMarkOrderCompleted={resolvedPaymentOrder.orderType === 'event' ? async (order) => { await handleMarkEventOrderServed(order); } : undefined}
          integrationSettings={integrationSettings}
        />
      )}

      {showUberDoordashModal && (
        <UberDoordashOrderModal
          onConfirm={(platform, customerName) => {
            setUberDoordashDetails({ platform, customerName });
            setOrderType('uber-doordash');
            setShowUberDoordashModal(false);
          }}
          onCancel={() => {
            setShowUberDoordashModal(false);
            if (waitstaffMode && !uberDoordashDetails) setWaitstaffTab('home');
          }}
        />
      )}

      {meatOptionItem && (
        <MeatOptionModal
          item={meatOptionItem}
          onSelect={(_, option) => addToCart(meatOptionItem, option)}
          onCancel={() => setMeatOptionItem(null)}
        />
      )}

      {liveFishItem && (
        <LiveFishModal
          item={liveFishItem}
          onConfirm={confirmLiveFish}
          onCancel={() => setLiveFishItem(null)}
        />
      )}

      {receiptQrOrder && (
        <ReceiptQrModal
          order={receiptQrOrder}
          businessInfo={businessInfo}
          tokenDisplay={
            receiptQrOrder.orderType === 'event'
              ? formatEventTokenBanner(getEventTokenDisplayLabel(receiptQrOrder, eventTokenLabelById) ?? '') || undefined
              : undefined
          }
          onClose={() => setReceiptQrOrder(null)}
        />
      )}

      {/* Hidden receipt for print */}
      <div className="hidden">
        {(placedOrder || orderToPrint) && (
          <div ref={receiptRef}>
            <PrintableReceipt order={orderToPrint || placedOrder!} businessInfo={businessInfo} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MobilePOSPage;
